/**
 * Elite Batch 9 Part 1 — Workflow Templates 748–767
 * HR (748-751), Industry-Specific (752-767): Training, Vendor, Visa, Academic,
 * Airport, Carrier, Clinical, Cold Chain, Court, Cross Dock, Customs, Donation,
 * Donor, Field Service, Fleet, Flight, Freight workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function bambooNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.bamboohr.com/api/gateway.php/{bamboo_subdomain}/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Basic {bamboo_api_key}" },
          { key: "Accept", value: "application/json" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function hubspotNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function pushNode(
  id: string,
  label: string,
  y: number,
  title: string,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "pushNotification",
    label,
    y,
    {
      pushNotificationDetails: { title, body: message },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 9 PART 1 — TEMPLATES 748–767
// =============================================================================

export const ELITE_BATCH9_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 748. TRAINING FEEDBACK BOT (8 nodes)
  // ===================================================================

  "Training Feedback Bot": {
    summary:
      "Automated training feedback collection and analysis. A session webhook captures training completion, BambooHR pulls the attendee roster, AI generates personalized feedback prompts, multi-channel delivery sends prompts via email and Slack, responses are collected and AI analyzes sentiment, a low-sentiment condition triggers escalation, feedback is aggregated to Google Sheets, and a summary notification is sent to the trainer and HR.",
    nodes: [
      trigger("Webhook: Training session completed", "webhook_app", {
        source: "LMS / Training Platform",
        event: "training.session.completed",
        howItStarts:
          "Your LMS or training platform fires a webhook when a training session is marked as completed by the facilitator",
        setup:
          "Configure your LMS (e.g., TalentLMS, Docebo) to POST a webhook when sessions complete. Payload should include session ID, trainer info, and topic details.",
        inputFields: [],
      }),

      // 2. BambooHR attendee roster
      aiNode(
        "2",
        "AI: Extract session details",
        280,
        "You are a training data extractor. Read the incoming webhook payload from an LMS and extract the session details regardless of payload format. Find the session_id, session_title, trainer_name, trainer_email, session_date, department, and attendee_emails (as comma-separated list). Respond JSON: {session_id: string, session_title: string, trainer_name: string, trainer_email: string, session_date: string, department: string, attendee_emails: string, attendee_count: number}",
        "{webhook_payload}",
        [
          { field: "session_id", as: "session_id", type: "string" },
          { field: "session_title", as: "session_title", type: "string" },
          { field: "trainer_name", as: "trainer_name", type: "string" },
          { field: "trainer_email", as: "trainer_email", type: "string" },
          { field: "session_date", as: "session_date", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "attendee_emails", as: "attendee_emails", type: "string" },
          { field: "attendee_count", as: "attendee_count", type: "number" },
        ],
      ),

      // 3. AI feedback prompts
      aiNode(
        "3",
        "AI: Generate personalized feedback prompts",
        520,
        "You are an HR feedback specialist. Generate a personalized training feedback survey for the given session. Create a concise feedback message with 5 key questions covering content relevance, trainer effectiveness, pace, practical applicability, and suggestions. Tailor questions to the session topic and department. Include a satisfaction score request (1-10). Respond JSON: {feedback_subject: string, feedback_body: string, slack_feedback_prompt: string, survey_link_params: string}",
        "Session: {session_title}. Trainer: {trainer_name}. Date: {session_date}. Department: {department}. Attendee count: {attendee_count}.",
        [
          { field: "feedback_subject", as: "feedback_subject", type: "string" },
          { field: "feedback_body", as: "feedback_body", type: "string" },
          {
            field: "slack_feedback_prompt",
            as: "slack_feedback_prompt",
            type: "string",
          },
          {
            field: "survey_link_params",
            as: "survey_link_params",
            type: "string",
          },
        ],
      ),

      // 4. Multi-channel delivery: email
      emailNode(
        "4",
        "Send feedback survey via email",
        760,
        "{attendee_emails}",
        "{feedback_subject}",
        "{feedback_body}",
      ),

      // 5. Multi-channel delivery: Slack
      slackNode(
        "5",
        "Post feedback prompt to Slack",
        1000,
        "{slack_feedback_prompt}",
      ),

      // 6. AI sentiment analysis on collected responses
      aiNode(
        "6",
        "AI: Analyze feedback sentiment",
        1240,
        "You are a training feedback analyst. Analyze the aggregated feedback responses for this training session. Calculate an overall sentiment score from 0-100, identify key themes (positive and negative), flag any concerning patterns, and determine if trainer coaching is needed. Respond JSON: {overall_sentiment: number, positive_themes: string, negative_themes: string, coaching_needed: string, summary_paragraph: string, average_rating: number}",
        "Session: {session_title}. Trainer: {trainer_name}. Department: {department}. Attendee count: {attendee_count}. Session ID: {session_id}.",
        [
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "number",
          },
          { field: "positive_themes", as: "positive_themes", type: "string" },
          { field: "negative_themes", as: "negative_themes", type: "string" },
          { field: "coaching_needed", as: "coaching_needed", type: "string" },
          {
            field: "summary_paragraph",
            as: "summary_paragraph",
            type: "string",
          },
          { field: "average_rating", as: "average_rating", type: "number" },
        ],
      ),

      // 7. Low sentiment condition
      n("7", "condition", "Is sentiment below threshold?", 1480, {
        conditionData: [
          {
            variable: "overall_sentiment",
            value: "50",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Feedback aggregation + trainer/HR summary (Sheets + notification combined)
      sheetsNode(
        "8",
        "Log feedback to Sheets + notify trainer/HR",
        1720,
        "TrainingFeedback",
        '[["{session_id}","{session_title}","{trainer_name}","{session_date}","{department}","{attendee_count}","{overall_sentiment}","{average_rating}","{positive_themes}","{negative_themes}","{coaching_needed}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // low sentiment -> log + alert
      e("7", "8", "output_2"), // normal sentiment -> still log
    ],
  },

  // ===================================================================
  // 749. VENDOR ONBOARDING FORM (9 nodes)
  // ===================================================================

  "Vendor Onboarding Form": {
    summary:
      "Automated vendor onboarding with validation and compliance screening. A form submission webhook captures vendor data, AI validates documents via OCR, runCode checks for duplicate vendors, a duplicate condition routes accordingly, compliance screening runs against sanctions lists, a risk threshold gate evaluates compliance score, vendor record creation occurs in the ERP, a welcome email is sent, and procurement is notified via Slack.",
    nodes: [
      trigger("Webhook: Vendor onboarding form submitted", "webhook_form", {
        source: "Vendor Portal / Web Form",
        event: "vendor.form.submitted",
        howItStarts:
          "A prospective vendor submits the onboarding form with company details, tax documents, and certifications",
        setup:
          "Create a vendor onboarding form in your portal. Configure it to POST all form fields and uploaded document URLs to your workflow webhook URL.",
        inputFields: [],
      }),

      // 2. AI document validation (OCR)
      aiNode(
        "2",
        "AI: Validate vendor documents (OCR)",
        280,
        "You are a vendor document validation specialist. Analyze the submitted vendor onboarding form payload. Extract the vendor company name, tax_id (EIN/TIN), contact_name, contact_email, contact_phone, business_type, w9_document_url, insurance_cert_url, address, bank_routing, and bank_account_last4. Validate that required fields are present and tax ID format is correct. Respond JSON: {vendor_name: string, tax_id: string, contact_name: string, contact_email: string, contact_phone: string, business_type: string, w9_url: string, insurance_url: string, address: string, bank_routing: string, bank_account_last4: string, validation_status: string, missing_fields: string}",
        "{webhook_payload}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "tax_id", as: "tax_id", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_phone", as: "contact_phone", type: "string" },
          { field: "business_type", as: "business_type", type: "string" },
          { field: "w9_url", as: "w9_url", type: "string" },
          { field: "insurance_url", as: "insurance_url", type: "string" },
          { field: "address", as: "vendor_address", type: "string" },
          {
            field: "validation_status",
            as: "validation_status",
            type: "string",
          },
          { field: "missing_fields", as: "missing_fields", type: "string" },
        ],
      ),

      // 3. Duplicate vendor detection (runCode)
      n("3", "runCode", "Check for duplicate vendors", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simulate duplicate vendor check against existing vendor database
const vendorName = (vars.vendor_name || "").toLowerCase().trim();
const taxId = (vars.tax_id || "").replace(/[^0-9]/g, "");
// In production this would query your ERP/database
// For now, we check basic format validity as a proxy
const hasTaxId = taxId.length === 9;
const hasName = vendorName.length > 2;
const isDuplicate = false; // Will be replaced by actual DB lookup
const duplicateScore = isDuplicate ? 100 : 0;
const normalizedName = vendorName.replace(/[^a-z0-9]/g, "");
const normalizedTaxId = taxId;
return {
  is_duplicate: isDuplicate ? "yes" : "no",
  duplicate_score: duplicateScore,
  normalized_name: normalizedName,
  normalized_tax_id: normalizedTaxId,
  validation_passed: hasTaxId && hasName ? "yes" : "no"
};`,
          outputVariables: [
            { name: "is_duplicate", type: "string" },
            { name: "duplicate_score", type: "number" },
            { name: "normalized_name", type: "string" },
            { name: "normalized_tax_id", type: "string" },
            { name: "validation_passed", type: "string" },
          ],
        },
      }),

      // 4. Duplicate condition
      n("4", "condition", "Is vendor a duplicate?", 760, {
        conditionData: [
          {
            variable: "is_duplicate",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Compliance screening (OFAC / sanctions)
      n(
        "5",
        "hitApi",
        "Screen vendor against sanctions lists",
        1000,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.ofac-api.com/v4/search?name={vendor_name}&source=SDN,EU,UN&type=individual,entity&minScore=85",
            headers: [
              { key: "Authorization", value: "Bearer {ofac_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "matches",
                customName: "sanctions_matches_json",
                type: "string",
              },
              {
                name: "totalMatches",
                customName: "sanctions_match_count",
                type: "number",
              },
              {
                name: "highestScore",
                customName: "sanctions_highest_score",
                type: "number",
              },
            ],
          },
        },
        280,
      ),

      // 6. Risk threshold gate
      n("6", "condition", "Is compliance risk acceptable?", 1240, {
        conditionData: [
          {
            variable: "sanctions_match_count",
            value: "0",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Vendor record creation (ERP / NetSuite)
      n("7", "hitApi", "Create vendor record in NetSuite", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{netsuite_account}.suitetalk.api.netsuite.com/services/rest/record/v1/vendor",
          headers: [
            { key: "Authorization", value: "Bearer {netsuite_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "companyName", value: "{vendor_name}" },
            { key: "email", value: "{contact_email}" },
            { key: "phone", value: "{contact_phone}" },
            { key: "taxIdNum", value: "{tax_id}" },
            { key: "defaultAddress", value: "{vendor_address}" },
            { key: "category", value: "{business_type}" },
          ],
          variables: [
            { name: "id", customName: "vendor_record_id", type: "string" },
            {
              name: "companyName",
              customName: "created_vendor_name",
              type: "string",
            },
          ],
        },
      }),

      // 8. Welcome email
      emailNode(
        "8",
        "Send vendor welcome email",
        1720,
        "{contact_email}",
        "Welcome to Our Vendor Network - {vendor_name}",
        "Dear {contact_name},\\n\\nWelcome! Your vendor onboarding for {vendor_name} has been approved and your account has been created.\\n\\nVendor ID: {vendor_record_id}\\nBusiness Type: {business_type}\\n\\nNext steps:\\n1. Log in to our vendor portal to complete your profile\\n2. Upload any remaining certifications\\n3. Review our vendor handbook and payment terms\\n\\nOur procurement team will reach out shortly to discuss purchase order processes.\\n\\nThank you for partnering with us!",
      ),

      // 9. Procurement Slack notification
      slackNode(
        "9",
        "Notify procurement team via Slack",
        1960,
        "New Vendor Onboarded: {vendor_name}\\nVendor ID: {vendor_record_id}\\nContact: {contact_name} ({contact_email})\\nBusiness Type: {business_type}\\nCompliance: Passed (0 sanctions matches)\\nTax ID: ***{normalized_tax_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "9", "output_1"), // duplicate -> notify procurement of duplicate
      e("4", "5", "output_2"), // not duplicate -> compliance screening
      e("5", "6"),
      e("6", "7", "output_1"), // compliant -> create record
      e("6", "9", "output_2"), // non-compliant -> alert procurement
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 750. VENDOR ONBOARDING WORKFLOW (10 nodes)
  // ===================================================================

  "Vendor Onboarding Workflow": {
    summary:
      "End-to-end vendor onboarding with task management and compliance tracking. A vendor approval trigger starts the process, runCode generates a task template, DocuSign dispatches the NDA for signature, a delay monitors signature status, a timeout condition handles non-signers, portal access is provisioned, compliance training is assigned, progress is tracked in Sheets, a welcome sequence is sent, and a completion notification goes to stakeholders.",
    nodes: [
      trigger("Webhook: Vendor approved for onboarding", "webhook_app", {
        source: "Procurement System / ERP",
        event: "vendor.approved",
        howItStarts:
          "Procurement team approves a new vendor, triggering the full onboarding workflow with task assignments and compliance requirements",
        setup:
          "Configure your procurement system to fire a webhook when a vendor transitions to 'approved' status. Include vendor ID, name, contact details, and contract type.",
        inputFields: [],
      }),

      // 2. Task template generation (runCode)
      n("2", "runCode", "Generate onboarding task template", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse vendor approval payload and generate onboarding task list
const payload = JSON.parse(vars.webhook_payload || "{}");
const vendor = payload.vendor || payload;
const vendorName = vendor.name || vendor.company_name || vendor.companyName || "";
const vendorEmail = vendor.email || vendor.contact_email || "";
const contactName = vendor.contact_name || vendor.contactName || "";
const vendorId = vendor.id || vendor.vendor_id || "";
const contractType = vendor.contract_type || vendor.contractType || "standard";
const category = vendor.category || "general";
const tasks = [
  "NDA signature",
  "Portal access setup",
  "Compliance training completion",
  "Banking information verification",
  "Insurance certificate upload",
  "W-9 form verification",
  "First PO test transaction"
];
const taskCount = tasks.length;
const taskListStr = tasks.join(", ");
const estimatedDays = contractType === "enterprise" ? 14 : 7;
return {
  vendor_name: vendorName,
  vendor_email: vendorEmail,
  contact_name: contactName,
  vendor_id: vendorId,
  contract_type: contractType,
  vendor_category: category,
  task_list: taskListStr,
  task_count: taskCount,
  estimated_completion_days: estimatedDays,
  onboarding_deadline: new Date(Date.now() + estimatedDays * 86400000).toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "vendor_name", type: "string" },
            { name: "vendor_email", type: "string" },
            { name: "contact_name", type: "string" },
            { name: "vendor_id", type: "string" },
            { name: "contract_type", type: "string" },
            { name: "vendor_category", type: "string" },
            { name: "task_list", type: "string" },
            { name: "task_count", type: "number" },
            { name: "estimated_completion_days", type: "number" },
            { name: "onboarding_deadline", type: "string" },
          ],
        },
      }),

      // 3. DocuSign NDA dispatch
      n("3", "hitApi", "Send NDA via DocuSign", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://na4.docusign.net/restapi/v2.1/accounts/{docusign_account_id}/envelopes",
          headers: [
            { key: "Authorization", value: "Bearer {docusign_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "emailSubject",
              value: "NDA Required: Vendor Onboarding for {vendor_name}",
            },
            { key: "templateId", value: "{docusign_nda_template_id}" },
            {
              key: "templateRoles",
              value:
                '[{"email":"{vendor_email}","name":"{contact_name}","roleName":"Signer"}]',
            },
            { key: "status", value: "sent" },
          ],
          variables: [
            { name: "envelopeId", customName: "envelope_id", type: "string" },
            { name: "status", customName: "envelope_status", type: "string" },
            {
              name: "statusDateTime",
              customName: "envelope_sent_at",
              type: "string",
            },
          ],
        },
      }),

      // 4. Signature status monitor (delay)
      n("4", "delayAction", "Wait 72 hours for NDA signature", 760, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 72,
          delayUnit: "hours",
        },
      }),

      // 5. Signature timeout condition
      n("5", "condition", "Was NDA signed within deadline?", 1000, {
        conditionData: [
          {
            variable: "envelope_status",
            value: "completed",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Portal access provisioning
      n("6", "hitApi", "Provision vendor portal access", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{vendor_portal_domain}/api/v1/vendors/provision",
          headers: [
            { key: "Authorization", value: "Bearer {portal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vendor_id", value: "{vendor_id}" },
            { key: "email", value: "{vendor_email}" },
            { key: "name", value: "{contact_name}" },
            { key: "company", value: "{vendor_name}" },
            { key: "role", value: "vendor_standard" },
          ],
          variables: [
            {
              name: "portal_user_id",
              customName: "portal_user_id",
              type: "string",
            },
            {
              name: "temporary_password",
              customName: "temp_password",
              type: "string",
            },
            {
              name: "portal_url",
              customName: "portal_login_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. Compliance training assignment
      n("7", "hitApi", "Assign compliance training (LMS)", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.talentlms.com/v1/addusertocourse",
          headers: [
            { key: "Authorization", value: "Basic {talentlms_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "user_email", value: "{vendor_email}" },
            { key: "course_id", value: "{vendor_compliance_course_id}" },
            { key: "role", value: "learner" },
          ],
          variables: [
            {
              name: "enrollment_id",
              customName: "training_enrollment_id",
              type: "string",
            },
            {
              name: "course_name",
              customName: "training_course_name",
              type: "string",
            },
          ],
        },
      }),

      // 8. Progress dashboard (Sheets)
      sheetsNode(
        "8",
        "Log onboarding progress to Sheets",
        1720,
        "VendorOnboarding",
        '[["{vendor_id}","{vendor_name}","{contact_name}","{vendor_email}","{contract_type}","{envelope_status}","{portal_user_id}","{training_enrollment_id}","{task_count}","{onboarding_deadline}"]]',
      ),

      // 9. Welcome sequence email
      emailNode(
        "9",
        "Send vendor welcome sequence",
        1960,
        "{vendor_email}",
        "Welcome to Our Vendor Network - Onboarding Checklist for {vendor_name}",
        "Dear {contact_name},\\n\\nWelcome to our vendor network! Your onboarding is underway.\\n\\nVendor ID: {vendor_id}\\nContract Type: {contract_type}\\n\\nOnboarding Checklist:\\n{task_list}\\n\\nDeadline: {onboarding_deadline}\\n\\nPortal Access:\\nURL: {portal_login_url}\\nTemporary Password: {temp_password}\\n\\nCompliance Training: {training_course_name} has been assigned. Please complete it by {onboarding_deadline}.\\n\\nFor questions, contact our procurement team.",
      ),

      // 10. Completion notification
      slackNode(
        "10",
        "Notify stakeholders of onboarding start",
        2200,
        "Vendor Onboarding Initiated:\\nVendor: {vendor_name} (ID: {vendor_id})\\nContact: {contact_name}\\nContract: {contract_type}\\nNDA: {envelope_status}\\nPortal: Provisioned\\nTraining: {training_course_name} assigned\\nDeadline: {onboarding_deadline}\\nTasks: {task_count} items",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // signed -> provision access
      e("5", "9", "output_2"), // not signed -> send reminder email
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 751. VISA AND WORK AUTHORIZATION TRACKER (8 nodes)
  // ===================================================================

  "Visa and Work Authorization Tracker": {
    summary:
      "Proactive visa and work authorization monitoring. A daily schedule triggers BambooHR to query employee work authorizations, runCode classifies expiration windows into critical/warning/upcoming, a condition routes critical expirations for immediate notification, warning emails are sent to employees and HR, immigration case status is checked, a compliance dashboard logs all data to Sheets, and a monthly summary report is emailed to HR leadership.",
    nodes: [
      trigger("Daily visa expiration check", "schedule", {
        schedule: "0 8 * * 1-5",
        howItStarts:
          "Runs every weekday at 8 AM to check all employee work authorizations for upcoming expirations",
        setup:
          "Ensure BambooHR has work authorization data populated for all employees. Configure the schedule to run on business days.",
        inputFields: [],
      }),

      // 2. BambooHR authorization query
      bambooNode(
        "2",
        "Query employee work authorizations (BambooHR)",
        280,
        "GET",
        "reports/custom?format=JSON&fields=displayName,workEmail,supervisorEmail,workAuthorizationType,workAuthorizationExpirationDate,workAuthorizationStatus,department",
        [],
        [
          {
            name: "employees",
            customName: "auth_employees_json",
            type: "string",
          },
          { name: "fields", customName: "auth_fields", type: "string" },
        ],
      ),

      // 3. Expiration window classification (runCode)
      n("3", "runCode", "Classify expiration urgency", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse employee authorization data and classify by urgency
const employees = JSON.parse(vars.auth_employees_json || "[]");
const now = new Date();
const criticalDays = 30;
const warningDays = 90;
let criticalList = [];
let warningList = [];
let upcomingList = [];
let totalTracked = 0;

for (const emp of employees) {
  const expDate = emp.workAuthorizationExpirationDate || emp.expirationDate;
  if (!expDate) continue;
  totalTracked++;
  const exp = new Date(expDate);
  const daysUntil = Math.floor((exp - now) / 86400000);
  const entry = emp.displayName + " (" + (emp.workEmail || "") + ") - " + daysUntil + " days - " + (emp.workAuthorizationType || "Unknown");
  if (daysUntil <= criticalDays) criticalList.push(entry);
  else if (daysUntil <= warningDays) warningList.push(entry);
  else if (daysUntil <= 180) upcomingList.push(entry);
}
return {
  critical_employees: criticalList.join("; ") || "none",
  critical_count: criticalList.length,
  warning_employees: warningList.join("; ") || "none",
  warning_count: warningList.length,
  upcoming_employees: upcomingList.join("; ") || "none",
  upcoming_count: upcomingList.length,
  total_tracked: totalTracked,
  has_critical: criticalList.length > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "critical_employees", type: "string" },
            { name: "critical_count", type: "number" },
            { name: "warning_employees", type: "string" },
            { name: "warning_count", type: "number" },
            { name: "upcoming_employees", type: "string" },
            { name: "upcoming_count", type: "number" },
            { name: "total_tracked", type: "number" },
            { name: "has_critical", type: "string" },
          ],
        },
      }),

      // 4. Critical expiration condition
      n("4", "condition", "Any critical expirations (< 30 days)?", 760, {
        conditionData: [
          {
            variable: "has_critical",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Warning notification (email)
      emailNode(
        "5",
        "Send critical expiration alert",
        1000,
        "{hr_immigration_email}",
        "URGENT: {critical_count} Work Authorizations Expiring Within 30 Days",
        "CRITICAL ALERT: The following employees have work authorizations expiring within 30 days:\\n\\n{critical_employees}\\n\\nImmediate action required:\\n1. Contact immigration attorney for renewal filings\\n2. Notify affected employees and managers\\n3. Prepare backup staffing plans if renewals are delayed\\n\\nWarning (30-90 days): {warning_count} employees\\n{warning_employees}\\n\\nUpcoming (90-180 days): {upcoming_count} employees\\nTotal tracked: {total_tracked}",
      ),

      // 6. Immigration case status check
      n("6", "hitApi", "Check immigration case status (USCIS)", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.uscis-status.com/v1/cases?status=pending&company_id={company_id}",
          headers: [
            { key: "Authorization", value: "Bearer {uscis_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "cases", customName: "pending_cases_json", type: "string" },
            {
              name: "totalCases",
              customName: "pending_case_count",
              type: "number",
            },
          ],
        },
      }),

      // 7. Compliance dashboard (Sheets)
      sheetsNode(
        "7",
        "Update compliance dashboard (Sheets)",
        1480,
        "VisaCompliance",
        '[["{today_date}","{total_tracked}","{critical_count}","{warning_count}","{upcoming_count}","{pending_case_count}","{critical_employees}"]]',
      ),

      // 8. Monthly report (email)
      emailNode(
        "8",
        "Send compliance summary to HR leadership",
        1720,
        "{hr_leadership_email}",
        "Work Authorization Compliance Report - {total_tracked} Employees Tracked",
        "Work Authorization Compliance Summary:\\n\\nTotal employees with tracked authorizations: {total_tracked}\\n\\nCritical (< 30 days): {critical_count}\\n{critical_employees}\\n\\nWarning (30-90 days): {warning_count}\\n{warning_employees}\\n\\nUpcoming (90-180 days): {upcoming_count}\\n{upcoming_employees}\\n\\nPending USCIS Cases: {pending_case_count}\\n\\nPlease review and ensure all renewal filings are on track.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // critical -> urgent alert
      e("4", "6", "output_2"), // no critical -> skip to case check
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 752. ACADEMIC ADVISING CHATBOT (9 nodes)
  // ===================================================================

  "Academic Advising Chatbot": {
    summary:
      "AI-powered academic advising assistant for students. A student chat trigger starts the conversation, SIS records are looked up, AI classifies the student intent (course selection, degree progress, scheduling), a degree audit is queried, AI generates course recommendations, runCode detects schedule conflicts, an advisor escalation condition routes complex cases, the CRM is updated with interaction details, and an appointment is booked if needed.",
    nodes: [
      trigger("Webhook: Student advising chat message", "webhook_app", {
        source: "Student Portal / Chat Widget",
        event: "advising.chat.message",
        howItStarts:
          "A student initiates or continues an advising conversation through the university portal or chat widget",
        setup:
          "Embed the advising chatbot widget on your student portal. Configure it to POST student messages with their student ID to the workflow webhook URL.",
        inputFields: [],
      }),

      // 2. SIS record lookup
      aiNode(
        "2",
        "AI: Extract student info and query SIS",
        280,
        "You are a university advising assistant intake processor. Read the incoming chat payload and extract the student_id, student_name, student_email, major, academic_year (freshman/sophomore/junior/senior), current_gpa, and the student_question. Determine the intent: course_selection, degree_progress, scheduling, transfer_credit, or general. Respond JSON: {student_id: string, student_name: string, student_email: string, major: string, academic_year: string, current_gpa: string, student_question: string, intent: string}",
        "{webhook_payload}",
        [
          { field: "student_id", as: "student_id", type: "string" },
          { field: "student_name", as: "student_name", type: "string" },
          { field: "student_email", as: "student_email", type: "string" },
          { field: "major", as: "student_major", type: "string" },
          { field: "academic_year", as: "academic_year", type: "string" },
          { field: "current_gpa", as: "current_gpa", type: "string" },
          { field: "student_question", as: "student_question", type: "string" },
          { field: "intent", as: "advising_intent", type: "string" },
        ],
      ),

      // 3. SIS degree audit query
      n("3", "hitApi", "Query degree audit from SIS", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{sis_domain}/api/v1/students/{student_id}/degree-audit?major={student_major}",
          headers: [
            { key: "Authorization", value: "Bearer {sis_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "completedCredits",
              customName: "completed_credits",
              type: "number",
            },
            {
              name: "requiredCredits",
              customName: "required_credits",
              type: "number",
            },
            {
              name: "remainingCourses",
              customName: "remaining_courses_json",
              type: "string",
            },
            {
              name: "coreCompleted",
              customName: "core_completed",
              type: "string",
            },
            {
              name: "electivesNeeded",
              customName: "electives_needed",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI intent classification + degree audit analysis
      aiNode(
        "4",
        "AI: Analyze degree audit and classify intent",
        760,
        "You are an academic advisor AI. Based on the student question, their degree audit data, and intent, provide comprehensive advising. For course_selection: recommend specific courses from their remaining requirements. For degree_progress: summarize completion status. For scheduling: suggest a balanced schedule. Determine complexity (simple/complex) and whether a human advisor is needed. Respond JSON: {response_message: string, recommended_courses: string, completion_percentage: number, complexity: string, needs_human_advisor: string, advisor_reason: string, suggested_semester_plan: string}",
        "Student: {student_name}, Major: {student_major}, Year: {academic_year}, GPA: {current_gpa}. Question: {student_question}. Intent: {advising_intent}. Credits completed: {completed_credits}/{required_credits}. Remaining courses: {remaining_courses_json}. Core completed: {core_completed}. Electives needed: {electives_needed}.",
        [
          {
            field: "response_message",
            as: "advising_response",
            type: "string",
          },
          {
            field: "recommended_courses",
            as: "recommended_courses",
            type: "string",
          },
          {
            field: "completion_percentage",
            as: "completion_percentage",
            type: "number",
          },
          { field: "complexity", as: "advising_complexity", type: "string" },
          {
            field: "needs_human_advisor",
            as: "needs_human_advisor",
            type: "string",
          },
          { field: "advisor_reason", as: "advisor_reason", type: "string" },
          {
            field: "suggested_semester_plan",
            as: "suggested_semester_plan",
            type: "string",
          },
        ],
      ),

      // 5. Schedule conflict detection (runCode)
      n("5", "runCode", "Detect schedule conflicts", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check recommended courses for scheduling conflicts
const recommended = (vars.recommended_courses || "").split(",").map(c => c.trim());
const existingSchedule = vars.remaining_courses_json || "[]";
// In production, this checks actual course times against student's registered courses
const hasConflict = false; // Would be real time-overlap check
const conflictDetails = hasConflict ? "Time conflict detected" : "No conflicts found";
const courseCount = recommended.length;
const creditEstimate = courseCount * 3; // Estimate 3 credits per course
return {
  has_schedule_conflict: hasConflict ? "yes" : "no",
  conflict_details: conflictDetails,
  recommended_course_count: courseCount,
  estimated_credits: creditEstimate,
  schedule_feasible: creditEstimate <= 18 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "has_schedule_conflict", type: "string" },
            { name: "conflict_details", type: "string" },
            { name: "recommended_course_count", type: "number" },
            { name: "estimated_credits", type: "number" },
            { name: "schedule_feasible", type: "string" },
          ],
        },
      }),

      // 6. Advisor escalation condition
      n("6", "condition", "Does student need human advisor?", 1240, {
        conditionData: [
          {
            variable: "needs_human_advisor",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. CRM update
      n("7", "hitApi", "Update student CRM record", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{crm_domain}/api/v1/students/{student_id}/interactions",
          headers: [
            { key: "Authorization", value: "Bearer {crm_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "type", value: "advising_chat" },
            { key: "intent", value: "{advising_intent}" },
            { key: "response", value: "{advising_response}" },
            { key: "recommended_courses", value: "{recommended_courses}" },
            { key: "escalated", value: "{needs_human_advisor}" },
            { key: "complexity", value: "{advising_complexity}" },
          ],
          variables: [
            {
              name: "interaction_id",
              customName: "interaction_id",
              type: "string",
            },
          ],
        },
      }),

      // 8. Send response to student
      n("8", "sendMessage", "Send advising response to student", 1720, {
        sendMessageDetails: {
          message:
            "{advising_response}\\n\\nRecommended courses: {recommended_courses}\\nSchedule conflicts: {conflict_details}\\nDegree completion: {completion_percentage}%\\nSuggested plan: {suggested_semester_plan}",
        },
      }),

      // 9. Appointment booking (if escalated)
      n("9", "hitApi", "Book advisor appointment (Calendly)", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduled_events",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "event_type", value: "{advising_event_type_uri}" },
            { key: "invitee_email", value: "{student_email}" },
            { key: "invitee_name", value: "{student_name}" },
            {
              key: "questions_and_answers",
              value:
                '[{"question":"Reason for visit","answer":"{advisor_reason}"}]',
            },
          ],
          variables: [
            { name: "uri", customName: "appointment_uri", type: "string" },
            {
              name: "start_time",
              customName: "appointment_time",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "9", "output_1"), // needs human -> book appointment
      e("6", "7", "output_2"), // AI sufficient -> update CRM
      e("9", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 753. AIRPORT TRANSFER SCHEDULER (8 nodes)
  // ===================================================================

  "Airport Transfer Scheduler": {
    summary:
      "End-to-end airport transfer booking and management. A transfer request trigger captures booking details, FlightAware verifies flight status, vehicle availability is checked, runCode handles driver assignment and route optimization, a no-availability condition manages overbooking, booking confirmation is emailed, flight delay adjustments are monitored, and post-transfer feedback and billing are triggered.",
    nodes: [
      trigger("Webhook: Airport transfer request", "webhook_app", {
        source: "Booking Portal / Travel Platform",
        event: "transfer.request.created",
        howItStarts:
          "A traveler or travel agent submits an airport transfer request through the booking portal",
        setup:
          "Configure your booking portal to POST transfer requests including passenger details, flight info, pickup/dropoff locations, and vehicle preferences to the workflow URL.",
        inputFields: [],
      }),

      // 2. Extract + Flight status verification (FlightAware API)
      aiNode(
        "2",
        "AI: Extract transfer details",
        280,
        "You are a travel booking data extractor. Parse the incoming transfer request payload and extract: passenger_name, passenger_email, passenger_phone, flight_number, flight_date, airline, arrival_or_departure (arrival/departure), pickup_location, dropoff_location, pickup_time, passenger_count, vehicle_preference (sedan/suv/van/limo), special_requests, and booking_reference. Respond JSON: {passenger_name: string, passenger_email: string, passenger_phone: string, flight_number: string, flight_date: string, airline: string, transfer_direction: string, pickup_location: string, dropoff_location: string, pickup_time: string, passenger_count: number, vehicle_preference: string, special_requests: string, booking_reference: string}",
        "{webhook_payload}",
        [
          { field: "passenger_name", as: "passenger_name", type: "string" },
          { field: "passenger_email", as: "passenger_email", type: "string" },
          { field: "passenger_phone", as: "passenger_phone", type: "string" },
          { field: "flight_number", as: "flight_number", type: "string" },
          { field: "flight_date", as: "flight_date", type: "string" },
          { field: "airline", as: "airline", type: "string" },
          {
            field: "transfer_direction",
            as: "transfer_direction",
            type: "string",
          },
          { field: "pickup_location", as: "pickup_location", type: "string" },
          { field: "dropoff_location", as: "dropoff_location", type: "string" },
          { field: "pickup_time", as: "pickup_time", type: "string" },
          { field: "passenger_count", as: "passenger_count", type: "number" },
          {
            field: "vehicle_preference",
            as: "vehicle_preference",
            type: "string",
          },
          { field: "special_requests", as: "special_requests", type: "string" },
          {
            field: "booking_reference",
            as: "booking_reference",
            type: "string",
          },
        ],
      ),

      // 3. FlightAware status lookup
      n("3", "hitApi", "Verify flight status (FlightAware)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://aeroapi.flightaware.com/aeroapi/flights/{flight_number}?start={flight_date}",
          headers: [
            { key: "x-apikey", value: "{flightaware_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "flights[0].status",
              customName: "flight_status",
              type: "string",
            },
            {
              name: "flights[0].estimated_in",
              customName: "estimated_arrival",
              type: "string",
            },
            {
              name: "flights[0].estimated_out",
              customName: "estimated_departure",
              type: "string",
            },
            {
              name: "flights[0].gate_origin",
              customName: "departure_gate",
              type: "string",
            },
            {
              name: "flights[0].gate_destination",
              customName: "arrival_gate",
              type: "string",
            },
            {
              name: "flights[0].terminal_destination",
              customName: "arrival_terminal",
              type: "string",
            },
          ],
        },
      }),

      // 4. Driver assignment + route optimization (runCode)
      n("4", "runCode", "Assign driver and optimize route", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assign available driver and calculate route/pricing
const vehicleType = vars.vehicle_preference || "sedan";
const passengerCount = parseInt(vars.passenger_count) || 1;
const pickup = vars.pickup_location || "";
const dropoff = vars.dropoff_location || "";
const flightStatus = vars.flight_status || "unknown";

// Vehicle capacity check
const capacity = { sedan: 3, suv: 5, van: 8, limo: 6 };
const maxCap = capacity[vehicleType] || 4;
const vehicleAvailable = passengerCount <= maxCap;

// Pricing estimate (would connect to real dispatch system)
const basePrices = { sedan: 65, suv: 85, van: 110, limo: 150 };
const basePrice = basePrices[vehicleType] || 75;
const estimatedPrice = basePrice + (flightStatus === "delayed" ? 0 : 0);

// Driver assignment (simulated - real system queries dispatch DB)
const driverAssigned = vehicleAvailable;
const driverName = driverAssigned ? "Available Driver" : "";
const driverPhone = driverAssigned ? "+1-555-0100" : "";
const estimatedDuration = "45 min"; // Would come from Google Maps API

return {
  driver_assigned: driverAssigned ? "yes" : "no",
  driver_name: driverName,
  driver_phone: driverPhone,
  vehicle_type_confirmed: vehicleType,
  estimated_price: estimatedPrice,
  estimated_duration: estimatedDuration,
  vehicle_available: vehicleAvailable ? "yes" : "no",
  adjusted_pickup_time: vars.pickup_time || ""
};`,
          outputVariables: [
            { name: "driver_assigned", type: "string" },
            { name: "driver_name", type: "string" },
            { name: "driver_phone", type: "string" },
            { name: "vehicle_type_confirmed", type: "string" },
            { name: "estimated_price", type: "number" },
            { name: "estimated_duration", type: "string" },
            { name: "vehicle_available", type: "string" },
            { name: "adjusted_pickup_time", type: "string" },
          ],
        },
      }),

      // 5. No availability condition
      n("5", "condition", "Is vehicle and driver available?", 1000, {
        conditionData: [
          {
            variable: "vehicle_available",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Booking confirmation (email)
      emailNode(
        "6",
        "Send booking confirmation email",
        1240,
        "{passenger_email}",
        "Airport Transfer Confirmed - Ref: {booking_reference}",
        "Dear {passenger_name},\\n\\nYour airport transfer has been confirmed!\\n\\nBooking Reference: {booking_reference}\\nFlight: {flight_number} ({airline})\\nFlight Status: {flight_status}\\nDirection: {transfer_direction}\\n\\nPickup: {pickup_location} at {adjusted_pickup_time}\\nDropoff: {dropoff_location}\\n\\nVehicle: {vehicle_type_confirmed}\\nDriver: {driver_name}\\nDriver Phone: {driver_phone}\\nEstimated Duration: {estimated_duration}\\nEstimated Price: ${estimated_price}\\n\\nTerminal: {arrival_terminal}, Gate: {arrival_gate}\\n\\nWe will notify you of any flight delays and adjust pickup accordingly.\\n\\nSpecial Requests: {special_requests}",
      ),

      // 7. Flight delay adjustment monitor
      n(
        "7",
        "delayAction",
        "Monitor for flight changes (2 hours before)",
        1480,
        {
          delayActionDetails: {
            delayType: "fixed",
            delayValue: 2,
            delayUnit: "hours",
          },
        },
      ),

      // 8. Post-transfer feedback + billing
      emailNode(
        "8",
        "Post-transfer feedback and billing",
        1720,
        "{passenger_email}",
        "How Was Your Transfer? - Ref: {booking_reference}",
        "Dear {passenger_name},\\n\\nThank you for using our airport transfer service!\\n\\nTrip Summary:\\nBooking: {booking_reference}\\nRoute: {pickup_location} to {dropoff_location}\\nVehicle: {vehicle_type_confirmed}\\nDriver: {driver_name}\\nAmount: ${estimated_price}\\n\\nWe would love your feedback! Please rate your experience:\\nhttps://feedback.example.com/rate?ref={booking_reference}\\n\\nThank you for choosing us!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // available -> confirm booking
      e("5", "8", "output_2"), // not available -> notify with alternatives
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 754. CARRIER PERFORMANCE SCORECARD (9 nodes)
  // ===================================================================

  "Carrier Performance Scorecard": {
    summary:
      "Weekly carrier performance evaluation and reporting. A weekly schedule triggers shipment data aggregation, claims and exceptions are fetched, runCode calculates performance scores, a score threshold condition flags underperformers, probation/suspension alerts go to Slack, scorecard reports log to Sheets, an executive dashboard is updated, and quarterly QBR triggers are managed.",
    nodes: [
      trigger("Weekly carrier performance evaluation", "schedule", {
        schedule: "0 6 * * 1",
        howItStarts:
          "Runs every Monday at 6 AM to evaluate all carrier performance from the previous week",
        setup:
          "Ensure your TMS has API access configured. Set up carrier master data with performance thresholds and contract SLAs.",
        inputFields: [],
      }),

      // 2. Shipment data aggregation
      n("2", "hitApi", "Aggregate weekly shipment data (TMS)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.project44.com/api/v4/shipments?status=delivered&dateRange=last7days&include=carrier,metrics",
          headers: [
            { key: "Authorization", value: "Bearer {project44_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "shipments", customName: "shipments_json", type: "string" },
            {
              name: "totalCount",
              customName: "total_shipments",
              type: "number",
            },
            { name: "carriers", customName: "carriers_json", type: "string" },
          ],
        },
      }),

      // 3. Claims/exception fetch
      n("3", "hitApi", "Fetch claims and exceptions (TMS)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.project44.com/api/v4/claims?dateRange=last7days&include=carrier,amount,type",
          headers: [
            { key: "Authorization", value: "Bearer {project44_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "claims", customName: "claims_json", type: "string" },
            { name: "totalClaims", customName: "total_claims", type: "number" },
            {
              name: "totalClaimAmount",
              customName: "total_claim_amount",
              type: "number",
            },
          ],
        },
      }),

      // 4. Performance score calculation (runCode)
      n("4", "runCode", "Calculate carrier performance scores", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate composite carrier performance scores
const shipments = JSON.parse(vars.shipments_json || "[]");
const claims = JSON.parse(vars.claims_json || "[]");
const totalShipments = parseInt(vars.total_shipments) || 0;
const totalClaims = parseInt(vars.total_claims) || 0;

// Group by carrier
const carrierStats = {};
for (const s of shipments) {
  const carrier = s.carrier || s.carrierName || "Unknown";
  if (!carrierStats[carrier]) carrierStats[carrier] = { shipments: 0, onTime: 0, claims: 0, claimAmount: 0 };
  carrierStats[carrier].shipments++;
  if (s.onTime || s.deliveredOnTime) carrierStats[carrier].onTime++;
}
for (const c of claims) {
  const carrier = c.carrier || c.carrierName || "Unknown";
  if (carrierStats[carrier]) {
    carrierStats[carrier].claims++;
    carrierStats[carrier].claimAmount += (c.amount || 0);
  }
}

let worstCarrier = "";
let worstScore = 100;
let belowThresholdCount = 0;
const threshold = 70;
const scorecardRows = [];

for (const [name, stats] of Object.entries(carrierStats)) {
  const otd = stats.shipments > 0 ? (stats.onTime / stats.shipments) * 100 : 0;
  const claimRate = stats.shipments > 0 ? (stats.claims / stats.shipments) * 100 : 0;
  const score = Math.round(otd * 0.6 + (100 - claimRate * 10) * 0.4);
  const finalScore = Math.max(0, Math.min(100, score));
  if (finalScore < worstScore) { worstScore = finalScore; worstCarrier = name; }
  if (finalScore < threshold) belowThresholdCount++;
  scorecardRows.push(name + ": " + finalScore + " (OTD: " + Math.round(otd) + "%, Claims: " + stats.claims + ")");
}

return {
  scorecard_summary: scorecardRows.join("; ") || "No carrier data",
  worst_carrier: worstCarrier,
  worst_score: worstScore,
  below_threshold_count: belowThresholdCount,
  has_underperformers: belowThresholdCount > 0 ? "yes" : "no",
  total_carriers_evaluated: Object.keys(carrierStats).length,
  performance_threshold: threshold
};`,
          outputVariables: [
            { name: "scorecard_summary", type: "string" },
            { name: "worst_carrier", type: "string" },
            { name: "worst_score", type: "number" },
            { name: "below_threshold_count", type: "number" },
            { name: "has_underperformers", type: "string" },
            { name: "total_carriers_evaluated", type: "number" },
            { name: "performance_threshold", type: "number" },
          ],
        },
      }),

      // 5. Score threshold condition
      n("5", "condition", "Any carriers below performance threshold?", 1000, {
        conditionData: [
          {
            variable: "has_underperformers",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Probation/suspension Slack alert
      slackNode(
        "6",
        "Alert: Carrier performance below threshold",
        1240,
        "CARRIER PERFORMANCE ALERT\\n\\nCarriers below {performance_threshold}% threshold: {below_threshold_count}\\nWorst performer: {worst_carrier} (Score: {worst_score})\\n\\nFull scorecard:\\n{scorecard_summary}\\n\\nTotal shipments this week: {total_shipments}\\nTotal claims: {total_claims} (${total_claim_amount})\\n\\nAction required: Review for probation or suspension.",
      ),

      // 7. Scorecard report (Sheets)
      sheetsNode(
        "7",
        "Log scorecard to Sheets",
        1480,
        "CarrierScorecard",
        '[["{today_date}","{total_carriers_evaluated}","{total_shipments}","{total_claims}","{total_claim_amount}","{below_threshold_count}","{worst_carrier}","{worst_score}","{scorecard_summary}"]]',
      ),

      // 8. Executive dashboard update
      emailNode(
        "8",
        "Send executive scorecard report",
        1720,
        "{logistics_leadership_email}",
        "Weekly Carrier Scorecard - {total_carriers_evaluated} Carriers Evaluated",
        "Weekly Carrier Performance Report\\n\\nCarriers Evaluated: {total_carriers_evaluated}\\nTotal Shipments: {total_shipments}\\nTotal Claims: {total_claims} (${total_claim_amount})\\n\\nUnderperformers (< {performance_threshold}%): {below_threshold_count}\\nWorst: {worst_carrier} ({worst_score})\\n\\nFull Scorecard:\\n{scorecard_summary}\\n\\nReview the attached dashboard for detailed metrics.",
      ),

      // 9. Quarterly QBR trigger
      n("9", "hitApi", "Trigger quarterly QBR scheduling (Calendly)", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduled_events",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "event_type", value: "{qbr_event_type_uri}" },
            { key: "invitee_email", value: "{worst_carrier_contact_email}" },
            {
              key: "questions_and_answers",
              value:
                '[{"question":"Review topic","answer":"Performance review: score {worst_score}, {below_threshold_count} weeks below threshold"}]',
            },
          ],
          variables: [
            { name: "uri", customName: "qbr_meeting_uri", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // underperformers -> Slack alert
      e("5", "7", "output_2"), // all good -> log only
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 755. CLINICAL TRIAL ELIGIBILITY SCREENER (10 nodes)
  // ===================================================================

  "Clinical Trial Eligibility Screener": {
    summary:
      "HIPAA-compliant clinical trial eligibility screening. A screening request triggers EHR data retrieval via FHIR API, trial protocol is fetched, AI matches patient eligibility criteria, a threshold condition routes eligible candidates, regulatory compliance validation runs via runCode, a coordinator is assigned, physician review goes through an approval gate, HIPAA-safe patient communication is sent, and audit trail logging captures all actions.",
    nodes: [
      trigger("Webhook: Clinical trial screening request", "webhook_app", {
        source: "Clinical Trial Management System (CTMS)",
        event: "screening.request.created",
        howItStarts:
          "A clinician or coordinator submits a patient screening request for a specific clinical trial through the CTMS",
        setup:
          "Configure your CTMS (e.g., Medidata Rave, Veeva Vault) to POST screening requests with patient MRN and trial ID to the workflow URL. Ensure HIPAA BAA is in place.",
        inputFields: [],
      }),

      // 2. EHR data retrieval (FHIR API)
      aiNode(
        "2",
        "AI: Extract screening request data",
        280,
        "You are a clinical trial data processor. Parse the incoming screening request and extract: patient_mrn (medical record number), trial_id, trial_name, requesting_physician, physician_email, coordinator_email, site_id, and screening_reason. Do NOT include any PHI in your response beyond the MRN. Respond JSON: {patient_mrn: string, trial_id: string, trial_name: string, requesting_physician: string, physician_email: string, coordinator_email: string, site_id: string, screening_reason: string}",
        "{webhook_payload}",
        [
          { field: "patient_mrn", as: "patient_mrn", type: "string" },
          { field: "trial_id", as: "trial_id", type: "string" },
          { field: "trial_name", as: "trial_name", type: "string" },
          {
            field: "requesting_physician",
            as: "requesting_physician",
            type: "string",
          },
          { field: "physician_email", as: "physician_email", type: "string" },
          {
            field: "coordinator_email",
            as: "coordinator_email",
            type: "string",
          },
          { field: "site_id", as: "site_id", type: "string" },
          { field: "screening_reason", as: "screening_reason", type: "string" },
        ],
      ),

      // 3. FHIR patient data fetch
      n("3", "hitApi", "Retrieve patient data (FHIR R4)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{fhir_server}/fhir/R4/Patient?identifier={patient_mrn}&_revinclude=Condition:patient&_revinclude=MedicationRequest:patient&_revinclude=Observation:patient",
          headers: [
            { key: "Authorization", value: "Bearer {fhir_access_token}" },
            { key: "Accept", value: "application/fhir+json" },
          ],
          body: [],
          variables: [
            { name: "entry", customName: "fhir_bundle_json", type: "string" },
            { name: "total", customName: "fhir_record_count", type: "number" },
          ],
        },
      }),

      // 4. Trial protocol fetch
      n("4", "hitApi", "Fetch trial protocol and criteria (CTMS)", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://{ctms_domain}/api/v1/trials/{trial_id}/eligibility-criteria",
          headers: [
            { key: "Authorization", value: "Bearer {ctms_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "inclusionCriteria",
              customName: "inclusion_criteria_json",
              type: "string",
            },
            {
              name: "exclusionCriteria",
              customName: "exclusion_criteria_json",
              type: "string",
            },
            { name: "phase", customName: "trial_phase", type: "string" },
            {
              name: "targetEnrollment",
              customName: "target_enrollment",
              type: "number",
            },
            {
              name: "currentEnrollment",
              customName: "current_enrollment",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI eligibility matching
      aiNode(
        "5",
        "AI: Match patient to trial criteria",
        1000,
        "You are a clinical trial eligibility specialist. Compare the patient FHIR data against the trial inclusion/exclusion criteria. For each criterion, determine if the patient meets (PASS), fails (FAIL), or has insufficient data (UNKNOWN). Calculate an overall eligibility score (0-100). Flag any safety concerns. IMPORTANT: Do not include PHI in your response — use coded references only. Respond JSON: {eligibility_score: number, meets_inclusion: string, exclusion_flags: string, criteria_results_summary: string, safety_concerns: string, recommendation: string, unknown_criteria_count: number}",
        "Patient FHIR bundle: {fhir_bundle_json}. Trial: {trial_name} (Phase {trial_phase}). Inclusion: {inclusion_criteria_json}. Exclusion: {exclusion_criteria_json}.",
        [
          {
            field: "eligibility_score",
            as: "eligibility_score",
            type: "number",
          },
          { field: "meets_inclusion", as: "meets_inclusion", type: "string" },
          { field: "exclusion_flags", as: "exclusion_flags", type: "string" },
          {
            field: "criteria_results_summary",
            as: "criteria_results_summary",
            type: "string",
          },
          { field: "safety_concerns", as: "safety_concerns", type: "string" },
          {
            field: "recommendation",
            as: "eligibility_recommendation",
            type: "string",
          },
          {
            field: "unknown_criteria_count",
            as: "unknown_criteria_count",
            type: "number",
          },
        ],
      ),

      // 6. Eligibility threshold condition
      n("6", "condition", "Does patient meet eligibility threshold?", 1240, {
        conditionData: [
          {
            variable: "eligibility_score",
            value: "70",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Regulatory compliance validation (runCode)
      n("7", "runCode", "Validate regulatory compliance", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate regulatory and enrollment constraints
const currentEnrollment = parseInt(vars.current_enrollment) || 0;
const targetEnrollment = parseInt(vars.target_enrollment) || 100;
const eligibilityScore = parseInt(vars.eligibility_score) || 0;
const unknownCount = parseInt(vars.unknown_criteria_count) || 0;
const safetyConcerns = vars.safety_concerns || "none";

const enrollmentOpen = currentEnrollment < targetEnrollment;
const hasUnresolvedCriteria = unknownCount > 2;
const hasSafetyConcerns = safetyConcerns !== "none" && safetyConcerns !== "";
const regulatoryApproved = enrollmentOpen && !hasUnresolvedCriteria && !hasSafetyConcerns;

return {
  enrollment_open: enrollmentOpen ? "yes" : "no",
  enrollment_slots_remaining: targetEnrollment - currentEnrollment,
  has_unresolved_criteria: hasUnresolvedCriteria ? "yes" : "no",
  has_safety_concerns: hasSafetyConcerns ? "yes" : "no",
  regulatory_approved: regulatoryApproved ? "yes" : "no",
  regulatory_notes: !enrollmentOpen ? "Trial enrollment full" : hasUnresolvedCriteria ? "Too many unknown criteria - manual review needed" : hasSafetyConcerns ? "Safety concerns flagged" : "All regulatory checks passed"
};`,
          outputVariables: [
            { name: "enrollment_open", type: "string" },
            { name: "enrollment_slots_remaining", type: "number" },
            { name: "has_unresolved_criteria", type: "string" },
            { name: "has_safety_concerns", type: "string" },
            { name: "regulatory_approved", type: "string" },
            { name: "regulatory_notes", type: "string" },
          ],
        },
      }),

      // 8. Physician review (approval gate)
      n("8", "approvalGate", "Physician review and approval", 1720, {
        approvalGateDetails: {
          approver: "{physician_email}",
          message:
            "Clinical Trial Screening Review\\n\\nTrial: {trial_name} (Phase {trial_phase})\\nPatient MRN: {patient_mrn}\\nEligibility Score: {eligibility_score}/100\\n\\nInclusion criteria met: {meets_inclusion}\\nExclusion flags: {exclusion_flags}\\nSafety concerns: {safety_concerns}\\nRecommendation: {eligibility_recommendation}\\n\\nRegulatory status: {regulatory_notes}\\nSlots remaining: {enrollment_slots_remaining}\\n\\nPlease review and approve/reject enrollment.",
          timeoutHours: 48,
        },
      }),

      // 9. HIPAA-safe patient communication
      emailNode(
        "9",
        "Send HIPAA-safe screening notification",
        1960,
        "{coordinator_email}",
        "Screening Result - Trial {trial_id} - MRN {patient_mrn}",
        "Clinical Trial Screening Notification\\n\\nTrial: {trial_name} ({trial_id})\\nPatient MRN: {patient_mrn}\\nSite: {site_id}\\n\\nEligibility Score: {eligibility_score}/100\\nRecommendation: {eligibility_recommendation}\\nPhysician Review: Completed by {requesting_physician}\\n\\nRegulatory Status: {regulatory_notes}\\nSlots remaining: {enrollment_slots_remaining}\\n\\nPlease proceed with informed consent process if approved.\\n\\nNote: This communication contains no PHI beyond MRN. Full details available in CTMS.",
      ),

      // 10. Audit trail logging (Sheets)
      sheetsNode(
        "10",
        "Log screening to audit trail (Sheets)",
        2200,
        "ClinicalTrialAudit",
        '[["{today_date}","{trial_id}","{trial_name}","{patient_mrn}","{site_id}","{eligibility_score}","{eligibility_recommendation}","{regulatory_approved}","{requesting_physician}","{regulatory_notes}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // eligible -> regulatory check
      e("6", "10", "output_2"), // not eligible -> log and close
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 756. COLD CHAIN TEMPERATURE MONITOR (8 nodes)
  // ===================================================================

  "Cold Chain Temperature Monitor": {
    summary:
      "Real-time cold chain temperature monitoring and excursion management. An IoT sensor trigger captures temperature readings, runCode evaluates thresholds, a condition detects excursions, critical alerts are sent via SMS/voice (Twilio), minor warnings go to Slack, automated corrective actions are dispatched, compliance data logs to Sheets, and a daily health report is emailed.",
    nodes: [
      trigger("Webhook: IoT temperature sensor reading", "webhook_app", {
        source: "IoT Gateway / Sensor Platform",
        event: "sensor.temperature.reading",
        howItStarts:
          "IoT temperature sensors in cold storage units, transport containers, or warehouses transmit readings to the workflow via webhook",
        setup:
          "Configure your IoT gateway (e.g., AWS IoT Core, Azure IoT Hub) to POST temperature readings including sensor_id, temperature, humidity, and location to the workflow URL. Set transmission interval to 5-15 minutes.",
        inputFields: [],
      }),

      // 2. Threshold evaluation (runCode)
      n("2", "runCode", "Evaluate temperature thresholds", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse sensor data and evaluate against cold chain thresholds
const payload = JSON.parse(vars.webhook_payload || "{}");
const sensorId = payload.sensor_id || payload.sensorId || payload.device_id || "";
const temperature = parseFloat(payload.temperature || payload.temp || payload.value || 0);
const humidity = parseFloat(payload.humidity || payload.rh || 0);
const location = payload.location || payload.zone || payload.unit_id || "";
const timestamp = payload.timestamp || new Date().toISOString();
const productType = payload.product_type || "pharmaceutical";

// Temperature thresholds by product type
const thresholds = {
  pharmaceutical: { min: 2, max: 8, criticalMin: 0, criticalMax: 12 },
  frozen: { min: -25, max: -15, criticalMin: -30, criticalMax: -10 },
  ambient: { min: 15, max: 25, criticalMin: 10, criticalMax: 30 }
};
const t = thresholds[productType] || thresholds.pharmaceutical;

const inRange = temperature >= t.min && temperature <= t.max;
const isCritical = temperature < t.criticalMin || temperature > t.criticalMax;
const isMinor = !inRange && !isCritical;
const deviation = inRange ? 0 : (temperature < t.min ? t.min - temperature : temperature - t.max);

return {
  sensor_id: sensorId,
  current_temp: temperature,
  current_humidity: humidity,
  sensor_location: location,
  reading_timestamp: timestamp,
  product_type: productType,
  temp_min: t.min,
  temp_max: t.max,
  is_in_range: inRange ? "yes" : "no",
  is_critical_excursion: isCritical ? "yes" : "no",
  is_minor_excursion: isMinor ? "yes" : "no",
  deviation_degrees: Math.round(deviation * 10) / 10,
  alert_level: isCritical ? "critical" : isMinor ? "warning" : "normal"
};`,
          outputVariables: [
            { name: "sensor_id", type: "string" },
            { name: "current_temp", type: "number" },
            { name: "current_humidity", type: "number" },
            { name: "sensor_location", type: "string" },
            { name: "reading_timestamp", type: "string" },
            { name: "product_type", type: "string" },
            { name: "temp_min", type: "number" },
            { name: "temp_max", type: "number" },
            { name: "is_in_range", type: "string" },
            { name: "is_critical_excursion", type: "string" },
            { name: "is_minor_excursion", type: "string" },
            { name: "deviation_degrees", type: "number" },
            { name: "alert_level", type: "string" },
          ],
        },
      }),

      // 3. Excursion detection condition
      n("3", "condition", "Is this a critical excursion?", 520, {
        conditionData: [
          {
            variable: "is_critical_excursion",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Critical SMS/voice escalation (Twilio)
      smsNode(
        "4",
        "Send critical temperature SMS alert (Twilio)",
        760,
        "{cold_chain_manager_phone}",
        "CRITICAL COLD CHAIN ALERT\\nSensor: {sensor_id}\\nLocation: {sensor_location}\\nTemp: {current_temp}C (Range: {temp_min}-{temp_max}C)\\nDeviation: {deviation_degrees}C\\nProduct: {product_type}\\nTime: {reading_timestamp}\\nIMMEDIATE ACTION REQUIRED",
      ),

      // 5. Minor Slack warning
      slackNode(
        "5",
        "Post minor excursion warning to Slack",
        760,
        "Cold Chain Warning\\nSensor: {sensor_id} ({sensor_location})\\nTemp: {current_temp}C (Range: {temp_min}-{temp_max}C)\\nDeviation: {deviation_degrees}C\\nProduct: {product_type}\\nAlert Level: {alert_level}\\nMonitoring continues.",
        480,
      ),

      // 6. Automated corrective action
      n("6", "hitApi", "Dispatch corrective action (HVAC/BMS)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{bms_api_domain}/api/v1/zones/{sensor_location}/adjust",
          headers: [
            { key: "Authorization", value: "Bearer {bms_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "target_temperature", value: "{temp_min}" },
            { key: "mode", value: "emergency_cool" },
            { key: "sensor_id", value: "{sensor_id}" },
            {
              key: "reason",
              value: "Automated excursion response - temp {current_temp}C",
            },
          ],
          variables: [
            {
              name: "action_id",
              customName: "corrective_action_id",
              type: "string",
            },
            { name: "status", customName: "corrective_status", type: "string" },
          ],
        },
      }),

      // 7. Compliance data logging (Sheets)
      sheetsNode(
        "7",
        "Log temperature data to compliance sheet",
        1240,
        "ColdChainLog",
        '[["{reading_timestamp}","{sensor_id}","{sensor_location}","{current_temp}","{current_humidity}","{temp_min}","{temp_max}","{alert_level}","{deviation_degrees}","{product_type}","{corrective_action_id}"]]',
      ),

      // 8. Daily health report (email)
      emailNode(
        "8",
        "Send daily cold chain health report",
        1480,
        "{quality_team_email}",
        "Cold Chain Daily Health Report - Sensor {sensor_id}",
        "Cold Chain Monitoring Report\\n\\nSensor: {sensor_id}\\nLocation: {sensor_location}\\nProduct Type: {product_type}\\n\\nLatest Reading:\\nTemperature: {current_temp}C\\nHumidity: {current_humidity}%\\nAcceptable Range: {temp_min}-{temp_max}C\\nStatus: {alert_level}\\nDeviation: {deviation_degrees}C\\n\\nCorrective Action: {corrective_status} (ID: {corrective_action_id})\\n\\nAll readings are logged for regulatory compliance.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // critical -> SMS alert
      e("3", "5", "output_2"), // not critical -> Slack warning
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 757. COURT DATE REMINDER SYSTEM (7 nodes)
  // ===================================================================

  "Court Date Reminder System": {
    summary:
      "Automated court date tracking and reminder system. A daily calendar sync pulls upcoming hearings, case management data is queried, runCode calculates reminder timelines, attorney and client notifications are sent via email, a missed acknowledgment condition handles non-responses, calendar sync updates schedules, and post-hearing follow-up triggers are sent.",
    nodes: [
      trigger("Daily court date check", "schedule", {
        schedule: "0 7 * * 1-5",
        howItStarts:
          "Runs every weekday at 7 AM to check for upcoming court dates within the notification window",
        setup:
          "Connect to your case management system API. Ensure all matters have court dates entered with hearing type, judge, and courtroom details.",
        inputFields: [],
      }),

      // 2. Case management query
      n("2", "hitApi", "Query upcoming hearings (Clio)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.clio.com/api/v4/calendar_entries.json?type=CourtDate&status=pending&from={today_date}&to={plus_30_days}&fields=id,summary,start_at,location,matter,attendees",
          headers: [
            { key: "Authorization", value: "Bearer {clio_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "hearings_json", type: "string" },
            {
              name: "meta.records",
              customName: "total_hearings",
              type: "number",
            },
          ],
        },
      }),

      // 3. Reminder timeline calculation (runCode)
      n("3", "runCode", "Calculate reminder timelines", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse hearings and determine which need reminders today
const hearings = JSON.parse(vars.hearings_json || "[]");
const now = new Date();
const reminders = [];
let urgentCount = 0;
let weekCount = 0;

for (const h of hearings) {
  const hearingDate = new Date(h.start_at || h.date);
  const daysUntil = Math.floor((hearingDate - now) / 86400000);
  const matterName = (h.matter || {}).display_number || h.summary || "";
  const location = h.location || "";
  const attendees = (h.attendees || []).map(a => a.name || a.email || "").join(", ");

  // Send reminders at 7 days, 3 days, and 1 day before
  if (daysUntil === 7 || daysUntil === 3 || daysUntil === 1) {
    reminders.push({
      matter: matterName,
      date: hearingDate.toISOString().split("T")[0],
      time: hearingDate.toISOString().split("T")[1].substring(0, 5),
      location: location,
      daysUntil: daysUntil,
      attendees: attendees,
      urgency: daysUntil === 1 ? "urgent" : "standard"
    });
    if (daysUntil <= 3) urgentCount++;
    else weekCount++;
  }
}

const reminderList = reminders.map(r => r.matter + " on " + r.date + " (" + r.daysUntil + " days) at " + r.location).join("; ");
return {
  reminders_to_send: reminders.length,
  urgent_reminders: urgentCount,
  standard_reminders: weekCount,
  reminder_details: reminderList || "No reminders needed today",
  has_reminders: reminders.length > 0 ? "yes" : "no",
  reminder_data_json: JSON.stringify(reminders)
};`,
          outputVariables: [
            { name: "reminders_to_send", type: "number" },
            { name: "urgent_reminders", type: "number" },
            { name: "standard_reminders", type: "number" },
            { name: "reminder_details", type: "string" },
            { name: "has_reminders", type: "string" },
            { name: "reminder_data_json", type: "string" },
          ],
        },
      }),

      // 4. Attorney/client notification (email)
      emailNode(
        "4",
        "Send court date reminder emails",
        760,
        "{firm_attorneys_email}",
        "Court Date Reminders - {reminders_to_send} Upcoming Hearings",
        "Court Date Reminders\\n\\nYou have {reminders_to_send} upcoming hearing(s) requiring attention:\\n\\n{reminder_details}\\n\\nUrgent (within 3 days): {urgent_reminders}\\nStandard (7 days out): {standard_reminders}\\n\\nPlease confirm your preparation status and ensure all filings are complete.\\n\\nReply CONFIRMED to acknowledge receipt of this reminder.",
      ),

      // 5. Missed acknowledgment condition
      n("5", "condition", "Any urgent unacknowledged reminders?", 1000, {
        conditionData: [
          {
            variable: "urgent_reminders",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Calendar sync update
      n("6", "hitApi", "Sync reminder status to calendar (Clio)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.clio.com/api/v4/calendar_entries.json",
          headers: [
            { key: "Authorization", value: "Bearer {clio_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"type":"CalendarEntry","attributes":{"summary":"Reminder sent: {reminder_details}","start_at":"{today_date}T09:00:00","end_at":"{today_date}T09:15:00"}}',
            },
          ],
          variables: [
            {
              name: "data.id",
              customName: "calendar_entry_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Post-hearing follow-up trigger
      emailNode(
        "7",
        "Schedule post-hearing follow-up",
        1480,
        "{firm_attorneys_email}",
        "Post-Hearing Follow-Up Required - {reminder_details}",
        "Post-Hearing Follow-Up Reminder\\n\\nThe following hearings are approaching. Please prepare post-hearing action items:\\n\\n{reminder_details}\\n\\nAfter each hearing, please:\\n1. Update case notes in Clio\\n2. File any court orders received\\n3. Notify clients of outcomes\\n4. Calendar any new deadlines\\n\\nTotal upcoming: {reminders_to_send}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // urgent -> calendar sync + escalation
      e("5", "7", "output_2"), // no urgent -> post-hearing prep
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 758. CROSS DOCK COORDINATION WORKFLOW (9 nodes)
  // ===================================================================

  "Cross Dock Coordination Workflow": {
    summary:
      "End-to-end cross-docking coordination for warehouse operations. An inbound ASN trigger captures shipment details, runCode assigns dock doors, outbound orders are matched, a match completeness condition handles partial matches, labor tasks are assigned, arrival delays are handled, carrier dispatch notifications are emailed, KPI tracking logs to Sheets, and exception reporting goes to Slack.",
    nodes: [
      trigger("Webhook: Inbound ASN received", "webhook_app", {
        source: "WMS / EDI Gateway",
        event: "asn.received",
        howItStarts:
          "An Advanced Shipping Notice (ASN) is received from an inbound carrier via EDI or WMS webhook, indicating a shipment is en route for cross-docking",
        setup:
          "Configure your EDI gateway or WMS to POST ASN (EDI 856) data to the workflow URL when inbound shipments are received. Include PO numbers, SKUs, quantities, and ETA.",
        inputFields: [],
      }),

      // 2. Dock door assignment (runCode)
      n("2", "runCode", "Assign dock door and parse ASN", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse ASN and assign optimal dock door for cross-docking
const payload = JSON.parse(vars.webhook_payload || "{}");
const asnId = payload.asn_id || payload.asnNumber || payload.shipmentId || "";
const carrierId = payload.carrier_id || payload.carrierSCAC || "";
const carrierName = payload.carrier_name || payload.carrierName || "";
const eta = payload.eta || payload.estimatedArrival || "";
const poNumbers = (payload.po_numbers || payload.purchaseOrders || []).join(", ");
const skuList = (payload.items || payload.lineItems || []);
const totalUnits = skuList.reduce((sum, item) => sum + (item.quantity || item.qty || 0), 0);
const totalPallets = Math.ceil(totalUnits / 48); // Estimate pallets
const skuSummary = skuList.map(i => (i.sku || i.itemNumber) + " x" + (i.quantity || i.qty)).join(", ");

// Dock door assignment logic (production: query WMS for available doors)
const doorPool = ["D1", "D2", "D3", "D4", "D5", "D6"];
const assignedDoor = doorPool[Math.floor(Math.random() * doorPool.length)];

return {
  asn_id: asnId,
  carrier_id: carrierId,
  carrier_name: carrierName,
  eta: eta,
  po_numbers: poNumbers,
  sku_summary: skuSummary,
  total_units: totalUnits,
  total_pallets: totalPallets,
  assigned_dock_door: assignedDoor,
  sku_count: skuList.length
};`,
          outputVariables: [
            { name: "asn_id", type: "string" },
            { name: "carrier_id", type: "string" },
            { name: "carrier_name", type: "string" },
            { name: "eta", type: "string" },
            { name: "po_numbers", type: "string" },
            { name: "sku_summary", type: "string" },
            { name: "total_units", type: "number" },
            { name: "total_pallets", type: "number" },
            { name: "assigned_dock_door", type: "string" },
            { name: "sku_count", type: "number" },
          ],
        },
      }),

      // 3. Outbound order matching
      n("3", "hitApi", "Match inbound to outbound orders (WMS)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{wms_domain}/api/v1/crossdock/match",
          headers: [
            { key: "Authorization", value: "Bearer {wms_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "asn_id", value: "{asn_id}" },
            { key: "po_numbers", value: "{po_numbers}" },
            { key: "skus", value: "{sku_summary}" },
          ],
          variables: [
            {
              name: "matchedOrders",
              customName: "matched_orders_json",
              type: "string",
            },
            {
              name: "matchPercentage",
              customName: "match_percentage",
              type: "number",
            },
            {
              name: "unmatchedSkus",
              customName: "unmatched_skus",
              type: "string",
            },
            {
              name: "outboundCarrier",
              customName: "outbound_carrier",
              type: "string",
            },
            {
              name: "outboundDoor",
              customName: "outbound_dock_door",
              type: "string",
            },
          ],
        },
      }),

      // 4. Match completeness condition
      n("4", "condition", "Are all SKUs matched to outbound orders?", 760, {
        conditionData: [
          {
            variable: "match_percentage",
            value: "95",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Labor task assignment
      n("5", "hitApi", "Assign cross-dock labor tasks (WMS)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{wms_domain}/api/v1/tasks/create",
          headers: [
            { key: "Authorization", value: "Bearer {wms_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "task_type", value: "crossdock_transfer" },
            { key: "asn_id", value: "{asn_id}" },
            { key: "from_door", value: "{assigned_dock_door}" },
            { key: "to_door", value: "{outbound_dock_door}" },
            { key: "pallets", value: "{total_pallets}" },
            { key: "priority", value: "high" },
          ],
          variables: [
            { name: "task_id", customName: "labor_task_id", type: "string" },
            {
              name: "assigned_team",
              customName: "assigned_team",
              type: "string",
            },
            {
              name: "estimated_minutes",
              customName: "estimated_minutes",
              type: "number",
            },
          ],
        },
      }),

      // 6. Arrival delay handling
      n("6", "delayAction", "Wait for carrier arrival check", 1240, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 30,
          delayUnit: "minutes",
        },
      }),

      // 7. Carrier dispatch notification (email)
      emailNode(
        "7",
        "Notify outbound carrier for dispatch",
        1480,
        "{outbound_carrier_email}",
        "Cross-Dock Ready for Pickup - ASN {asn_id}",
        "Cross-Dock Dispatch Notification\\n\\nASN: {asn_id}\\nInbound Carrier: {carrier_name}\\nDock Door (Inbound): {assigned_dock_door}\\nDock Door (Outbound): {outbound_dock_door}\\n\\nCargo Details:\\nSKUs: {sku_count}\\nTotal Units: {total_units}\\nPallets: {total_pallets}\\n\\nLabor Team: {assigned_team}\\nEstimated Transfer Time: {estimated_minutes} minutes\\nMatch Rate: {match_percentage}%\\n\\nPlease arrive at dock door {outbound_dock_door} for pickup.",
      ),

      // 8. KPI tracking (Sheets)
      sheetsNode(
        "8",
        "Log cross-dock KPIs to Sheets",
        1720,
        "CrossDockKPIs",
        '[["{today_date}","{asn_id}","{carrier_name}","{total_units}","{total_pallets}","{match_percentage}","{assigned_dock_door}","{outbound_dock_door}","{estimated_minutes}","{labor_task_id}"]]',
      ),

      // 9. Exception reporting (Slack)
      slackNode(
        "9",
        "Report cross-dock status to Slack",
        1960,
        "Cross-Dock Update: ASN {asn_id}\\nCarrier: {carrier_name}\\nDoor: {assigned_dock_door} -> {outbound_dock_door}\\nUnits: {total_units} | Pallets: {total_pallets}\\nMatch: {match_percentage}%\\nUnmatched: {unmatched_skus}\\nLabor: {assigned_team} (est. {estimated_minutes} min)",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // fully matched -> assign labor
      e("4", "9", "output_2"), // partial match -> exception report
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 759. CUSTOMS DOCUMENTATION GENERATOR (8 nodes)
  // ===================================================================

  "Customs Documentation Generator": {
    summary:
      "Automated customs documentation for international shipments. A shipment export trigger starts the process, AI classifies HS codes, trade compliance screening checks restricted parties, a compliance failure condition handles violations, runCode assembles the document package, customs broker transmission sends documents via email, the document archive logs to Sheets, and stakeholder notifications are sent.",
    nodes: [
      trigger("Webhook: Shipment marked for export", "webhook_app", {
        source: "ERP / TMS / Order Management",
        event: "shipment.export.created",
        howItStarts:
          "A shipment is flagged for international export, triggering customs documentation generation",
        setup:
          "Configure your ERP or TMS to POST shipment details when export status is set. Include product descriptions, values, origin/destination countries, and shipper/consignee details.",
        inputFields: [],
      }),

      // 2. AI HS code classification
      aiNode(
        "2",
        "AI: Classify HS codes and extract shipment data",
        280,
        "You are a customs classification specialist. Read the export shipment payload and extract shipment details. For each product, determine the correct HS (Harmonized System) code based on the product description. Also extract: shipper_name, shipper_address, shipper_country, consignee_name, consignee_address, destination_country, shipment_value, currency, incoterm, transport_mode, and items with their HS codes. Respond JSON: {shipment_id: string, shipper_name: string, shipper_address: string, shipper_country: string, consignee_name: string, consignee_address: string, destination_country: string, total_value: number, currency: string, incoterm: string, transport_mode: string, items_with_hs: string, item_count: number, broker_email: string}",
        "{webhook_payload}",
        [
          { field: "shipment_id", as: "shipment_id", type: "string" },
          { field: "shipper_name", as: "shipper_name", type: "string" },
          { field: "shipper_address", as: "shipper_address", type: "string" },
          { field: "shipper_country", as: "shipper_country", type: "string" },
          { field: "consignee_name", as: "consignee_name", type: "string" },
          {
            field: "consignee_address",
            as: "consignee_address",
            type: "string",
          },
          {
            field: "destination_country",
            as: "destination_country",
            type: "string",
          },
          { field: "total_value", as: "total_value", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "incoterm", as: "incoterm", type: "string" },
          { field: "transport_mode", as: "transport_mode", type: "string" },
          { field: "items_with_hs", as: "items_with_hs", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "broker_email", as: "broker_email", type: "string" },
        ],
      ),

      // 3. Trade compliance screening
      n("3", "hitApi", "Screen restricted parties (Visual Compliance)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.visualcompliance.com/v2/screen",
          headers: [
            { key: "Authorization", value: "Bearer {visual_compliance_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "parties",
              value:
                '[{"name":"{consignee_name}","address":"{consignee_address}","country":"{destination_country}"}]',
            },
            { key: "lists", value: '["SDN","DPL","EL","BIS_UVL"]' },
          ],
          variables: [
            {
              name: "results",
              customName: "screening_results_json",
              type: "string",
            },
            { name: "totalHits", customName: "screening_hits", type: "number" },
            {
              name: "overallStatus",
              customName: "compliance_status",
              type: "string",
            },
          ],
        },
      }),

      // 4. Compliance failure condition
      n("4", "condition", "Did compliance screening pass?", 760, {
        conditionData: [
          {
            variable: "screening_hits",
            value: "0",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Document assembly (runCode)
      n("5", "runCode", "Assemble customs document package", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assemble customs documentation package
const shipmentId = vars.shipment_id || "";
const shipperName = vars.shipper_name || "";
const consigneeName = vars.consignee_name || "";
const destination = vars.destination_country || "";
const totalValue = parseFloat(vars.total_value) || 0;
const currency = vars.currency || "USD";
const incoterm = vars.incoterm || "FOB";
const items = vars.items_with_hs || "";
const itemCount = parseInt(vars.item_count) || 0;
const transportMode = vars.transport_mode || "ocean";

// Determine required documents based on destination and value
const docs = ["Commercial Invoice", "Packing List"];
if (totalValue > 2500) docs.push("Shipper Export Declaration (SED/EEI)");
if (destination !== "CA" && destination !== "MX") docs.push("Certificate of Origin");
if (transportMode === "ocean") docs.push("Bill of Lading");
if (transportMode === "air") docs.push("Air Waybill");
docs.push("Customs Entry Form");

const documentList = docs.join(", ");
const needsLicense = totalValue > 50000 ? "yes" : "no";
const dutyEstimate = Math.round(totalValue * 0.05 * 100) / 100; // 5% estimate

return {
  document_list: documentList,
  document_count: docs.length,
  needs_export_license: needsLicense,
  estimated_duty: dutyEstimate,
  commercial_invoice_data: "Shipment " + shipmentId + ": " + shipperName + " to " + consigneeName + ", " + itemCount + " items, " + currency + " " + totalValue + ", " + incoterm,
  filing_deadline: new Date(Date.now() + 5 * 86400000).toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "document_list", type: "string" },
            { name: "document_count", type: "number" },
            { name: "needs_export_license", type: "string" },
            { name: "estimated_duty", type: "number" },
            { name: "commercial_invoice_data", type: "string" },
            { name: "filing_deadline", type: "string" },
          ],
        },
      }),

      // 6. Customs broker transmission (email)
      emailNode(
        "6",
        "Send documents to customs broker",
        1240,
        "{broker_email}",
        "Customs Documents Ready - Shipment {shipment_id} to {destination_country}",
        "Customs Documentation Package\\n\\nShipment ID: {shipment_id}\\nShipper: {shipper_name} ({shipper_country})\\nConsignee: {consignee_name} ({destination_country})\\nValue: {currency} {total_value}\\nIncoterm: {incoterm}\\nTransport: {transport_mode}\\n\\nItems: {items_with_hs}\\n\\nRequired Documents ({document_count}):\\n{document_list}\\n\\nCompliance Screening: PASSED (0 hits)\\nExport License Required: {needs_export_license}\\nEstimated Duty: {currency} {estimated_duty}\\nFiling Deadline: {filing_deadline}\\n\\nInvoice Summary: {commercial_invoice_data}\\n\\nPlease process and file with customs authority.",
      ),

      // 7. Document archive (Sheets)
      sheetsNode(
        "7",
        "Archive to customs documentation log (Sheets)",
        1480,
        "CustomsDocs",
        '[["{today_date}","{shipment_id}","{shipper_name}","{consignee_name}","{destination_country}","{total_value}","{currency}","{compliance_status}","{document_count}","{filing_deadline}","{estimated_duty}"]]',
      ),

      // 8. Stakeholder notification
      slackNode(
        "8",
        "Notify logistics team of customs filing",
        1720,
        "Customs Documentation Filed\\nShipment: {shipment_id}\\nRoute: {shipper_country} -> {destination_country}\\nValue: {currency} {total_value}\\nDocuments: {document_count} ({document_list})\\nCompliance: {compliance_status}\\nBroker notified: {broker_email}\\nDeadline: {filing_deadline}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // passed -> assemble docs
      e("4", "8", "output_2"), // failed -> alert team
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 760. DONATION CAMPAIGN BOT (8 nodes)
  // ===================================================================

  "Donation Campaign Bot": {
    summary:
      "Conversational donation collection bot with payment processing. A donor chat trigger captures the message, HubSpot looks up the donor CRM record, AI presents the campaign, donation intent is classified, Stripe processes the secure payment, a payment failure condition handles errors, runCode generates a tax receipt, and a campaign progress Slack alert tracks totals.",
    nodes: [
      trigger("Webhook: Donor chat message", "webhook_app", {
        source: "Website Chat / SMS / Messenger",
        event: "donation.chat.message",
        howItStarts:
          "A potential donor engages with the donation chatbot via website widget, SMS, or social messenger",
        setup:
          "Deploy the donation chatbot on your website, configure SMS (Twilio) and Messenger integrations to POST messages to the workflow URL.",
        inputFields: [],
      }),

      // 2. CRM donor lookup (HubSpot)
      aiNode(
        "2",
        "AI: Extract donor info from chat",
        280,
        "You are a nonprofit donation assistant. Read the incoming chat message and extract: donor_name, donor_email, donor_phone, message_text, and donation_intent (inquire, donate, recurring, pledge). If the donor mentions an amount, extract donation_amount. If they mention a campaign, extract campaign_name. Respond JSON: {donor_name: string, donor_email: string, donor_phone: string, message_text: string, donation_intent: string, donation_amount: number, campaign_name: string}",
        "{webhook_payload}",
        [
          { field: "donor_name", as: "donor_name", type: "string" },
          { field: "donor_email", as: "donor_email", type: "string" },
          { field: "donor_phone", as: "donor_phone", type: "string" },
          { field: "message_text", as: "message_text", type: "string" },
          { field: "donation_intent", as: "donation_intent", type: "string" },
          { field: "donation_amount", as: "donation_amount", type: "number" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
        ],
      ),

      // 3. HubSpot donor lookup
      hubspotNode(
        "3",
        "Look up donor in CRM (HubSpot)",
        520,
        "GET",
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{donor_email}"}]}]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "donor_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.total_donations",
            customName: "lifetime_donations",
            type: "number",
          },
          {
            name: "results[0].properties.last_donation_date",
            customName: "last_donation_date",
            type: "string",
          },
          {
            name: "results[0].properties.donor_tier",
            customName: "donor_tier",
            type: "string",
          },
        ],
      ),

      // 4. AI campaign presenter
      aiNode(
        "4",
        "AI: Present campaign and personalize ask",
        760,
        "You are a nonprofit fundraising assistant. Based on the donor history, tier, and intent, craft a personalized campaign pitch. If they are a returning donor, acknowledge their past support. Suggest an appropriate donation amount based on their tier and history. Include campaign impact metrics. For inquiries, provide campaign details. For donations, confirm the amount and prepare for payment. Respond JSON: {campaign_pitch: string, suggested_amount: number, impact_statement: string, payment_ready: string, campaign_goal: number, campaign_raised: number}",
        "Donor: {donor_name} ({donor_email}). Tier: {donor_tier}. Lifetime: ${lifetime_donations}. Last donation: {last_donation_date}. Intent: {donation_intent}. Requested amount: ${donation_amount}. Campaign: {campaign_name}. Message: {message_text}.",
        [
          { field: "campaign_pitch", as: "campaign_pitch", type: "string" },
          { field: "suggested_amount", as: "suggested_amount", type: "number" },
          { field: "impact_statement", as: "impact_statement", type: "string" },
          { field: "payment_ready", as: "payment_ready", type: "string" },
          { field: "campaign_goal", as: "campaign_goal", type: "number" },
          { field: "campaign_raised", as: "campaign_raised", type: "number" },
        ],
      ),

      // 5. Stripe secure payment
      n("5", "hitApi", "Process donation payment (Stripe)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.stripe.com/v1/payment_intents",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "amount", value: "{donation_amount}00" },
            { key: "currency", value: "usd" },
            { key: "receipt_email", value: "{donor_email}" },
            {
              key: "description",
              value: "Donation: {campaign_name} - {donor_name}",
            },
            { key: "metadata[campaign]", value: "{campaign_name}" },
            { key: "metadata[donor_email]", value: "{donor_email}" },
            { key: "metadata[hubspot_id]", value: "{hubspot_contact_id}" },
          ],
          variables: [
            { name: "id", customName: "payment_intent_id", type: "string" },
            { name: "status", customName: "payment_status", type: "string" },
            {
              name: "amount_received",
              customName: "amount_received",
              type: "number",
            },
          ],
        },
      }),

      // 6. Payment failure condition
      n("6", "condition", "Was payment successful?", 1240, {
        conditionData: [
          {
            variable: "payment_status",
            value: "succeeded",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Tax receipt generation (runCode)
      n("7", "runCode", "Generate tax receipt", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate tax-deductible donation receipt
const donorName = vars.donor_name || "";
const donorEmail = vars.donor_email || "";
const amount = parseFloat(vars.donation_amount) || 0;
const campaign = vars.campaign_name || "General Fund";
const paymentId = vars.payment_intent_id || "";
const today = new Date();
const receiptNumber = "RCP-" + today.getFullYear() + "-" + String(today.getMonth() + 1).padStart(2, "0") + "-" + Math.random().toString(36).substring(2, 8).toUpperCase();
const taxYear = today.getFullYear();

return {
  receipt_number: receiptNumber,
  receipt_date: today.toISOString().split("T")[0],
  tax_year: taxYear,
  tax_deductible_amount: amount,
  receipt_body: "Tax Receipt #" + receiptNumber + "\\nDate: " + today.toISOString().split("T")[0] + "\\nDonor: " + donorName + "\\nAmount: $" + amount.toFixed(2) + "\\nCampaign: " + campaign + "\\nPayment Ref: " + paymentId + "\\n\\nNo goods or services were provided in exchange for this contribution. This receipt may be used for tax deduction purposes.",
  org_ein: "{organization_ein}"
};`,
          outputVariables: [
            { name: "receipt_number", type: "string" },
            { name: "receipt_date", type: "string" },
            { name: "tax_year", type: "number" },
            { name: "tax_deductible_amount", type: "number" },
            { name: "receipt_body", type: "string" },
            { name: "org_ein", type: "string" },
          ],
        },
      }),

      // 8. Campaign progress Slack alert
      slackNode(
        "8",
        "Alert: Donation received for campaign",
        1720,
        "Donation Received!\\nDonor: {donor_name} ({donor_tier})\\nAmount: ${donation_amount}\\nCampaign: {campaign_name}\\nPayment: {payment_status} ({payment_intent_id})\\nReceipt: {receipt_number}\\nCampaign Progress: ${campaign_raised} / ${campaign_goal}\\nLifetime giving: ${lifetime_donations}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // payment succeeded -> generate receipt
      e("6", "8", "output_2"), // payment failed -> alert team
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 761. DONATION RECEIPT GENERATOR (7 nodes)
  // ===================================================================

  "Donation Receipt Generator": {
    summary:
      "Automated donation receipt generation from Stripe payments. A Stripe payment trigger captures the donation, HubSpot retrieves the donor profile, runCode validates receipt data, a validation failure condition handles errors, receipt PDF content is generated, multi-channel delivery sends via email, and the donation ledger is updated in Sheets.",
    nodes: [
      trigger("Webhook: Stripe donation payment completed", "webhook_app", {
        source: "Stripe",
        event: "payment_intent.succeeded",
        howItStarts:
          "A donation payment is successfully processed through Stripe, triggering automatic receipt generation",
        setup:
          "In Stripe Dashboard -> Webhooks -> Add endpoint -> URL: your workflow URL -> Select events: payment_intent.succeeded. Filter by metadata.type='donation' to only trigger for donations.",
        inputFields: [],
      }),

      // 2. Donor profile retrieval (HubSpot)
      aiNode(
        "2",
        "AI: Extract Stripe payment data",
        280,
        "You are a payment data extractor. Parse the Stripe webhook payload and extract: payment_id, amount (in dollars, not cents), currency, donor_email (from receipt_email or metadata), donor_name (from metadata or billing details), campaign_name (from metadata.campaign or description), payment_method_type, and created timestamp. Respond JSON: {payment_id: string, amount_dollars: number, currency: string, donor_email: string, donor_name: string, campaign_name: string, payment_method: string, payment_date: string}",
        "{webhook_payload}",
        [
          { field: "payment_id", as: "payment_id", type: "string" },
          { field: "amount_dollars", as: "amount_dollars", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "donor_email", as: "donor_email", type: "string" },
          { field: "donor_name", as: "donor_name", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "payment_method", as: "payment_method", type: "string" },
          { field: "payment_date", as: "payment_date", type: "string" },
        ],
      ),

      // 3. HubSpot donor profile
      hubspotNode(
        "3",
        "Retrieve donor profile (HubSpot)",
        520,
        "GET",
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{donor_email}"}]}]',
          },
        ],
        [
          { name: "results[0].id", customName: "hubspot_id", type: "string" },
          {
            name: "results[0].properties.address",
            customName: "donor_address",
            type: "string",
          },
          {
            name: "results[0].properties.total_donations",
            customName: "lifetime_total",
            type: "number",
          },
          {
            name: "results[0].properties.donor_tier",
            customName: "donor_tier",
            type: "string",
          },
        ],
      ),

      // 4. Receipt data validation (runCode)
      n("4", "runCode", "Validate receipt data", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate all required receipt fields
const name = vars.donor_name || "";
const email = vars.donor_email || "";
const amount = parseFloat(vars.amount_dollars) || 0;
const paymentId = vars.payment_id || "";
const campaign = vars.campaign_name || "General Fund";
const paymentDate = vars.payment_date || new Date().toISOString().split("T")[0];

const errors = [];
if (!name) errors.push("Missing donor name");
if (!email || !email.includes("@")) errors.push("Invalid donor email");
if (amount <= 0) errors.push("Invalid amount");
if (!paymentId) errors.push("Missing payment ID");

const receiptNum = "DON-" + new Date().getFullYear() + "-" + Math.random().toString(36).substring(2, 8).toUpperCase();
const isValid = errors.length === 0;

return {
  receipt_valid: isValid ? "yes" : "no",
  validation_errors: errors.join(", ") || "none",
  receipt_number: receiptNum,
  formatted_amount: "$" + amount.toFixed(2),
  formatted_date: paymentDate,
  campaign_final: campaign,
  receipt_year: new Date().getFullYear()
};`,
          outputVariables: [
            { name: "receipt_valid", type: "string" },
            { name: "validation_errors", type: "string" },
            { name: "receipt_number", type: "string" },
            { name: "formatted_amount", type: "string" },
            { name: "formatted_date", type: "string" },
            { name: "campaign_final", type: "string" },
            { name: "receipt_year", type: "number" },
          ],
        },
      }),

      // 5. Validation failure condition
      n("5", "condition", "Is receipt data valid?", 1000, {
        conditionData: [
          {
            variable: "receipt_valid",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Multi-channel delivery (email)
      emailNode(
        "6",
        "Send donation receipt via email",
        1240,
        "{donor_email}",
        "Donation Receipt {receipt_number} - Thank You, {donor_name}!",
        "Dear {donor_name},\\n\\nThank you for your generous donation!\\n\\nDONATION RECEIPT\\nReceipt Number: {receipt_number}\\nDate: {formatted_date}\\nAmount: {formatted_amount} {currency}\\nCampaign: {campaign_final}\\nPayment Method: {payment_method}\\nPayment Reference: {payment_id}\\n\\nNo goods or services were provided in exchange for this contribution.\\nEIN: {organization_ein}\\n\\nThis receipt is for your tax records ({receipt_year} tax year).\\n\\nYour lifetime giving: ${lifetime_total}\\nDonor Tier: {donor_tier}\\n\\nThank you for making a difference!",
      ),

      // 7. Donation ledger update (Sheets)
      sheetsNode(
        "7",
        "Update donation ledger (Sheets)",
        1480,
        "DonationLedger",
        '[["{formatted_date}","{receipt_number}","{donor_name}","{donor_email}","{formatted_amount}","{currency}","{campaign_final}","{payment_id}","{payment_method}","{donor_tier}","{lifetime_total}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // valid -> send receipt
      e("5", "7", "output_2"), // invalid -> log error
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 762. DONOR RENEWAL REMINDER (8 nodes)
  // ===================================================================

  "Donor Renewal Reminder": {
    summary:
      "Proactive donor renewal engagement workflow. A renewal window schedule triggers donor history pull from HubSpot, AI generates personalized impact stories, a donor tier condition routes major donors to staff tasks and mid/grassroots donors to email sequences, renewal conversion is tracked, and non-renewal follow-ups are triggered.",
    nodes: [
      trigger("Donor renewal window check", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Runs every Monday at 9 AM to identify donors approaching their annual renewal window (based on last donation date)",
        setup:
          "Configure donor lifecycle stages in HubSpot. Set renewal window to 11-13 months from last donation. Ensure donor tier property is populated.",
        inputFields: [],
      }),

      // 2. Donor history pull (HubSpot)
      hubspotNode(
        "2",
        "Pull donors in renewal window (HubSpot)",
        280,
        "POST",
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"last_donation_date","operator":"BETWEEN","value":"{renewal_window_start}","highValue":"{renewal_window_end}"},{"propertyName":"lifecyclestage","operator":"EQ","value":"donor"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","total_donations","last_donation_date","donor_tier","last_donation_amount","preferred_campaign"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          {
            name: "results",
            customName: "renewal_donors_json",
            type: "string",
          },
          { name: "total", customName: "renewal_donor_count", type: "number" },
        ],
      ),

      // 3. AI impact story
      aiNode(
        "3",
        "AI: Generate personalized impact stories",
        520,
        "You are a nonprofit storytelling specialist. Based on the donor data, generate personalized renewal messages. Create an impact narrative that connects their past giving to tangible outcomes. Tailor the tone by tier: major donors get exclusive updates, mid-tier get community stories, grassroots get mission highlights. Include a suggested renewal amount (typically 10-15% above last gift). Respond JSON: {impact_story: string, renewal_ask_amount: number, subject_line: string, personal_note: string, campaign_highlights: string, urgency_message: string}",
        "Donor count: {renewal_donor_count}. Sample donor data: {renewal_donors_json}.",
        [
          { field: "impact_story", as: "impact_story", type: "string" },
          {
            field: "renewal_ask_amount",
            as: "renewal_ask_amount",
            type: "number",
          },
          { field: "subject_line", as: "renewal_subject", type: "string" },
          { field: "personal_note", as: "personal_note", type: "string" },
          {
            field: "campaign_highlights",
            as: "campaign_highlights",
            type: "string",
          },
          { field: "urgency_message", as: "urgency_message", type: "string" },
        ],
      ),

      // 4. Donor tier condition
      n("4", "condition", "Is this a major donor (tier = major)?", 760, {
        conditionData: [
          {
            variable: "donor_tier",
            value: "major",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Major donor staff task (HubSpot) - True path
      hubspotNode(
        "5",
        "Create major donor outreach task (HubSpot)",
        1000,
        "POST",
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"Major Donor Renewal: Personal outreach needed","hs_task_body":"{personal_note}\\n\\nImpact Story: {impact_story}\\n\\nSuggested Ask: ${renewal_ask_amount}\\n\\nCampaign Highlights: {campaign_highlights}","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH","hs_timestamp":"{today_date}"}',
          },
        ],
        [
          { name: "id", customName: "task_id", type: "string" },
          {
            name: "properties.hs_task_subject",
            customName: "task_subject",
            type: "string",
          },
        ],
      ),

      // 6. Mid/grassroots email sequence - False path
      emailNode(
        "6",
        "Send renewal email to mid/grassroots donors",
        1000,
        "{donor_email}",
        "{renewal_subject}",
        "Dear {donor_first_name},\\n\\n{impact_story}\\n\\n{personal_note}\\n\\nYour Impact This Year:\\n{campaign_highlights}\\n\\n{urgency_message}\\n\\nRenew your support today with a gift of ${renewal_ask_amount}:\\nhttps://donate.example.com/renew?amount={renewal_ask_amount}&email={donor_email}\\n\\nEvery gift makes a difference. Thank you for being part of our community!\\n\\nWith gratitude,\\nThe Fundraising Team",
        480,
      ),

      // 7. Renewal conversion tracking
      sheetsNode(
        "7",
        "Track renewal outreach (Sheets)",
        1240,
        "DonorRenewals",
        '[["{today_date}","{renewal_donor_count}","{donor_tier}","{renewal_ask_amount}","{renewal_subject}","{impact_story}"]]',
      ),

      // 8. Non-renewal follow-up trigger
      n("8", "delayAction", "Wait 14 days for renewal response", 1480, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 14,
          delayUnit: "days",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // major donor -> staff task
      e("4", "6", "output_2"), // mid/grassroots -> email
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 763. DONOR THANK YOU SEQUENCE (8 nodes)
  // ===================================================================

  "Donor Thank You Sequence": {
    summary:
      "Multi-touch donor thank-you and engagement sequence. A donation confirmation trigger starts the flow, runCode segments the donor, AI generates a personalized thank-you message, an immediate gratitude email is sent, a day-3 impact email follows after a delay, a day-7 community email follows after another delay, engagement is tracked in Sheets, and a low engagement condition flags non-openers.",
    nodes: [
      trigger("Webhook: Donation confirmed", "webhook_app", {
        source: "Stripe / Payment Processor",
        event: "donation.confirmed",
        howItStarts:
          "A donation payment is confirmed, triggering an automated multi-touch thank-you and stewardship sequence",
        setup:
          "Configure Stripe webhooks to POST successful donation payments. Include donor details, amount, campaign, and payment method in the payload.",
        inputFields: [],
      }),

      // 2. Donor segmentation (runCode)
      n("2", "runCode", "Segment donor for personalization", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse donation and segment donor for thank-you personalization
const payload = JSON.parse(vars.webhook_payload || "{}");
const donorName = payload.donor_name || payload.name || (payload.billing_details || {}).name || "";
const donorEmail = payload.donor_email || payload.email || payload.receipt_email || "";
const amount = parseFloat(payload.amount || payload.amount_received || 0) / 100; // cents to dollars
const campaign = payload.campaign || (payload.metadata || {}).campaign || "General Fund";
const isRecurring = payload.recurring || (payload.metadata || {}).recurring === "true";
const isFirstTime = payload.first_donation || (payload.metadata || {}).first_time === "true";

// Segment based on amount and history
let segment = "grassroots";
if (amount >= 1000) segment = "major";
else if (amount >= 100) segment = "mid";

const thankYouTone = isFirstTime ? "warm_welcome" : isRecurring ? "loyal_appreciation" : "grateful";
const engagementLevel = segment === "major" ? "high_touch" : "standard";

return {
  donor_name: donorName,
  donor_email: donorEmail,
  donation_amount: amount,
  campaign_name: campaign,
  is_recurring: isRecurring ? "yes" : "no",
  is_first_time: isFirstTime ? "yes" : "no",
  donor_segment: segment,
  thank_you_tone: thankYouTone,
  engagement_level: engagementLevel,
  donation_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "donor_name", type: "string" },
            { name: "donor_email", type: "string" },
            { name: "donation_amount", type: "number" },
            { name: "campaign_name", type: "string" },
            { name: "is_recurring", type: "string" },
            { name: "is_first_time", type: "string" },
            { name: "donor_segment", type: "string" },
            { name: "thank_you_tone", type: "string" },
            { name: "engagement_level", type: "string" },
            { name: "donation_date", type: "string" },
          ],
        },
      }),

      // 3. AI personalized thank-you
      aiNode(
        "3",
        "AI: Generate personalized thank-you content",
        520,
        "You are a nonprofit stewardship specialist. Generate a 3-part thank-you sequence for this donor. Part 1 (immediate): heartfelt gratitude with specific impact of their gift amount. Part 2 (day 3): a short impact story showing their donation at work. Part 3 (day 7): community invitation and ways to stay engaged. Adjust tone based on segment and whether first-time/recurring. Respond JSON: {immediate_subject: string, immediate_body: string, day3_subject: string, day3_body: string, day7_subject: string, day7_body: string}",
        "Donor: {donor_name}. Amount: ${donation_amount}. Campaign: {campaign_name}. Segment: {donor_segment}. First time: {is_first_time}. Recurring: {is_recurring}. Tone: {thank_you_tone}.",
        [
          {
            field: "immediate_subject",
            as: "immediate_subject",
            type: "string",
          },
          { field: "immediate_body", as: "immediate_body", type: "string" },
          { field: "day3_subject", as: "day3_subject", type: "string" },
          { field: "day3_body", as: "day3_body", type: "string" },
          { field: "day7_subject", as: "day7_subject", type: "string" },
          { field: "day7_body", as: "day7_body", type: "string" },
        ],
      ),

      // 4. Immediate gratitude email
      emailNode(
        "4",
        "Send immediate thank-you email",
        760,
        "{donor_email}",
        "{immediate_subject}",
        "{immediate_body}",
      ),

      // 5. Day-3 impact email (delay)
      n("5", "delayAction", "Wait 3 days for impact email", 1000, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 3,
          delayUnit: "days",
        },
      }),

      // 6. Day-7 community email (delay + send)
      emailNode(
        "6",
        "Send day-3 impact story email",
        1240,
        "{donor_email}",
        "{day3_subject}",
        "{day3_body}",
      ),

      // 7. Engagement tracking (Sheets)
      n("7", "delayAction", "Wait 4 more days for community email", 1480, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 4,
          delayUnit: "days",
        },
      }),

      // 8. Low engagement condition
      emailNode(
        "8",
        "Send day-7 community invitation email",
        1720,
        "{donor_email}",
        "{day7_subject}",
        "{day7_body}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 764. FIELD SERVICE DISPATCH BOT (9 nodes)
  // ===================================================================

  "Field Service Dispatch Bot": {
    summary:
      "AI-powered field service dispatch and management. A service request trigger captures the issue, customer and equipment data is looked up, AI diagnoses the issue, a remote resolution condition checks if the problem can be fixed remotely, technician matching and dispatch handles field visits, a no-technician condition manages unavailability, dispatch confirmation is emailed, parts inventory is checked via Shopify, and post-service closure with feedback is triggered.",
    nodes: [
      trigger("Webhook: Service request submitted", "webhook_app", {
        source: "Customer Portal / CRM / Call Center",
        event: "service.request.created",
        howItStarts:
          "A customer submits a service request through the portal, or a call center agent creates one on their behalf",
        setup:
          "Configure your CRM or customer portal to POST service requests with customer ID, equipment details, and issue description to the workflow URL.",
        inputFields: [],
      }),

      // 2. Customer/equipment lookup
      aiNode(
        "2",
        "AI: Extract service request details",
        280,
        "You are a field service intake processor. Parse the incoming service request and extract: customer_name, customer_email, customer_phone, customer_address, equipment_model, equipment_serial, warranty_status, issue_description, urgency (low/medium/high/emergency), and preferred_date. Respond JSON: {customer_name: string, customer_email: string, customer_phone: string, customer_address: string, equipment_model: string, equipment_serial: string, warranty_status: string, issue_description: string, urgency: string, preferred_date: string}",
        "{webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "customer_address", as: "customer_address", type: "string" },
          { field: "equipment_model", as: "equipment_model", type: "string" },
          { field: "equipment_serial", as: "equipment_serial", type: "string" },
          { field: "warranty_status", as: "warranty_status", type: "string" },
          {
            field: "issue_description",
            as: "issue_description",
            type: "string",
          },
          { field: "urgency", as: "service_urgency", type: "string" },
          { field: "preferred_date", as: "preferred_date", type: "string" },
        ],
      ),

      // 3. AI issue diagnosis
      aiNode(
        "3",
        "AI: Diagnose issue and determine resolution path",
        520,
        "You are a field service diagnostic expert. Based on the equipment model, serial, and issue description, diagnose the likely problem. Determine if it can be resolved remotely (software update, settings change, user guidance) or requires a field visit. If field visit is needed, identify required skills, tools, and likely parts. Respond JSON: {diagnosis: string, root_cause: string, can_resolve_remotely: string, remote_instructions: string, required_skills: string, required_tools: string, likely_parts: string, estimated_repair_hours: number, difficulty_level: string}",
        "Equipment: {equipment_model} (S/N: {equipment_serial}). Warranty: {warranty_status}. Issue: {issue_description}. Urgency: {service_urgency}.",
        [
          { field: "diagnosis", as: "diagnosis", type: "string" },
          { field: "root_cause", as: "root_cause", type: "string" },
          {
            field: "can_resolve_remotely",
            as: "can_resolve_remotely",
            type: "string",
          },
          {
            field: "remote_instructions",
            as: "remote_instructions",
            type: "string",
          },
          { field: "required_skills", as: "required_skills", type: "string" },
          { field: "required_tools", as: "required_tools", type: "string" },
          { field: "likely_parts", as: "likely_parts", type: "string" },
          {
            field: "estimated_repair_hours",
            as: "estimated_repair_hours",
            type: "number",
          },
          { field: "difficulty_level", as: "difficulty_level", type: "string" },
        ],
      ),

      // 4. Remote resolution condition
      n("4", "condition", "Can issue be resolved remotely?", 760, {
        conditionData: [
          {
            variable: "can_resolve_remotely",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Technician matching + dispatch
      n("5", "hitApi", "Match and dispatch field technician", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{fsa_domain}/api/v1/dispatch/auto-assign",
          headers: [
            { key: "Authorization", value: "Bearer {fsa_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "customer_address", value: "{customer_address}" },
            { key: "required_skills", value: "{required_skills}" },
            { key: "urgency", value: "{service_urgency}" },
            { key: "preferred_date", value: "{preferred_date}" },
            { key: "estimated_hours", value: "{estimated_repair_hours}" },
          ],
          variables: [
            {
              name: "technician_name",
              customName: "tech_name",
              type: "string",
            },
            {
              name: "technician_phone",
              customName: "tech_phone",
              type: "string",
            },
            {
              name: "scheduled_date",
              customName: "scheduled_date",
              type: "string",
            },
            { name: "assigned", customName: "tech_assigned", type: "string" },
            {
              name: "work_order_id",
              customName: "work_order_id",
              type: "string",
            },
          ],
        },
      }),

      // 6. No technician condition
      n("6", "condition", "Was a technician assigned?", 1240, {
        conditionData: [
          {
            variable: "tech_assigned",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Dispatch confirmation (email)
      emailNode(
        "7",
        "Send dispatch confirmation to customer",
        1480,
        "{customer_email}",
        "Service Appointment Confirmed - Work Order {work_order_id}",
        "Dear {customer_name},\\n\\nYour service appointment has been scheduled.\\n\\nWork Order: {work_order_id}\\nEquipment: {equipment_model} (S/N: {equipment_serial})\\nDiagnosis: {diagnosis}\\n\\nTechnician: {tech_name}\\nPhone: {tech_phone}\\nScheduled Date: {scheduled_date}\\nEstimated Duration: {estimated_repair_hours} hours\\n\\nAddress: {customer_address}\\n\\nThe technician will bring: {required_tools}\\nLikely parts needed: {likely_parts}\\n\\nPlease ensure someone is available at the scheduled time.\\n\\nWarranty Status: {warranty_status}",
      ),

      // 8. Parts inventory check (Shopify)
      n("8", "hitApi", "Check parts inventory (Shopify)", 1720, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/products.json?title={likely_parts}&fields=id,title,variants",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "products",
              customName: "parts_inventory_json",
              type: "string",
            },
            {
              name: "products[0].variants[0].inventory_quantity",
              customName: "parts_in_stock",
              type: "number",
            },
          ],
        },
      }),

      // 9. Post-service closure + feedback
      emailNode(
        "9",
        "Send post-service feedback request",
        1960,
        "{customer_email}",
        "How Was Your Service? - Work Order {work_order_id}",
        "Dear {customer_name},\\n\\nWe hope your service experience was excellent!\\n\\nWork Order: {work_order_id}\\nTechnician: {tech_name}\\nEquipment: {equipment_model}\\nDiagnosis: {diagnosis}\\n\\nPlease take a moment to rate your experience:\\nhttps://feedback.example.com/rate?wo={work_order_id}\\n\\nYour feedback helps us improve our service quality.\\n\\nThank you for choosing us!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "7", "output_1"), // remote resolution -> send instructions via email
      e("4", "5", "output_2"), // needs field visit -> dispatch
      e("5", "6"),
      e("6", "7", "output_1"), // tech assigned -> confirm
      e("6", "9", "output_2"), // no tech -> notify customer of delay
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 765. FLEET MAINTENANCE SCHEDULER (8 nodes)
  // ===================================================================

  "Fleet Maintenance Scheduler": {
    summary:
      "Proactive fleet maintenance scheduling based on mileage and usage thresholds. A maintenance trigger captures vehicle alerts, runCode classifies priority, an immediate maintenance condition routes critical vehicles, service appointments are scheduled, replacement vehicles are assigned, driver and manager notifications are sent via email, parts are pre-ordered, and maintenance completion is logged to Sheets.",
    nodes: [
      trigger("Webhook: Vehicle maintenance threshold reached", "webhook_app", {
        source: "Fleet Telematics / GPS Tracker",
        event: "vehicle.maintenance.due",
        howItStarts:
          "Fleet telematics system fires a webhook when a vehicle reaches a maintenance milestone (mileage, engine hours, or time-based interval)",
        setup:
          "Configure your fleet telematics provider (e.g., Samsara, Geotab) to POST alerts when vehicles reach maintenance thresholds. Include vehicle ID, current odometer, engine hours, and last service date.",
        inputFields: [],
      }),

      // 2. Priority classification (runCode)
      n("2", "runCode", "Classify maintenance priority", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse vehicle maintenance alert and classify priority
const payload = JSON.parse(vars.webhook_payload || "{}");
const vehicleId = payload.vehicle_id || payload.vehicleId || payload.asset_id || "";
const vehicleName = payload.vehicle_name || payload.name || payload.label || "";
const driverName = payload.driver_name || payload.driverName || "";
const driverEmail = payload.driver_email || payload.driverEmail || "";
const managerEmail = payload.manager_email || payload.fleetManagerEmail || "";
const currentMileage = parseInt(payload.odometer || payload.mileage || 0);
const engineHours = parseInt(payload.engine_hours || payload.engineHours || 0);
const lastServiceDate = payload.last_service_date || payload.lastServiceDate || "";
const lastServiceMileage = parseInt(payload.last_service_mileage || 0);
const alertType = payload.alert_type || payload.maintenanceType || "routine";

// Priority classification
const milesSinceService = currentMileage - lastServiceMileage;
const daysSinceService = lastServiceDate ? Math.floor((new Date() - new Date(lastServiceDate)) / 86400000) : 999;
const isOverdue = milesSinceService > 7500 || daysSinceService > 180;
const isCritical = alertType === "brake" || alertType === "engine" || alertType === "tire" || isOverdue;
const priority = isCritical ? "immediate" : milesSinceService > 5000 ? "soon" : "scheduled";

const maintenanceItems = [];
if (milesSinceService >= 3000) maintenanceItems.push("Oil change");
if (milesSinceService >= 5000) maintenanceItems.push("Tire rotation");
if (milesSinceService >= 7500) maintenanceItems.push("Full inspection");
if (alertType === "brake") maintenanceItems.push("Brake service");
if (alertType === "engine") maintenanceItems.push("Engine diagnostic");

return {
  vehicle_id: vehicleId,
  vehicle_name: vehicleName,
  driver_name: driverName,
  driver_email: driverEmail,
  manager_email: managerEmail,
  current_mileage: currentMileage,
  engine_hours: engineHours,
  miles_since_service: milesSinceService,
  days_since_service: daysSinceService,
  maintenance_priority: priority,
  is_immediate: isCritical ? "yes" : "no",
  maintenance_items: maintenanceItems.join(", "),
  alert_type: alertType
};`,
          outputVariables: [
            { name: "vehicle_id", type: "string" },
            { name: "vehicle_name", type: "string" },
            { name: "driver_name", type: "string" },
            { name: "driver_email", type: "string" },
            { name: "manager_email", type: "string" },
            { name: "current_mileage", type: "number" },
            { name: "engine_hours", type: "number" },
            { name: "miles_since_service", type: "number" },
            { name: "days_since_service", type: "number" },
            { name: "maintenance_priority", type: "string" },
            { name: "is_immediate", type: "string" },
            { name: "maintenance_items", type: "string" },
            { name: "alert_type", type: "string" },
          ],
        },
      }),

      // 3. Immediate maintenance condition
      n("3", "condition", "Is maintenance priority immediate?", 520, {
        conditionData: [
          {
            variable: "is_immediate",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Service appointment scheduling
      n("4", "hitApi", "Schedule service appointment (Fleet API)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://{fleet_management_domain}/api/v1/service-orders",
          headers: [
            { key: "Authorization", value: "Bearer {fleet_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vehicle_id", value: "{vehicle_id}" },
            { key: "priority", value: "{maintenance_priority}" },
            { key: "items", value: "{maintenance_items}" },
            { key: "current_mileage", value: "{current_mileage}" },
            { key: "driver_email", value: "{driver_email}" },
          ],
          variables: [
            {
              name: "service_order_id",
              customName: "service_order_id",
              type: "string",
            },
            {
              name: "scheduled_date",
              customName: "service_date",
              type: "string",
            },
            { name: "shop_name", customName: "service_shop", type: "string" },
            {
              name: "estimated_cost",
              customName: "estimated_cost",
              type: "number",
            },
          ],
        },
      }),

      // 5. Replacement vehicle assignment
      n("5", "hitApi", "Assign replacement vehicle", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{fleet_management_domain}/api/v1/vehicles/assign-replacement",
          headers: [
            { key: "Authorization", value: "Bearer {fleet_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "original_vehicle_id", value: "{vehicle_id}" },
            { key: "driver_email", value: "{driver_email}" },
            { key: "needed_from", value: "{service_date}" },
          ],
          variables: [
            {
              name: "replacement_vehicle_id",
              customName: "replacement_id",
              type: "string",
            },
            {
              name: "replacement_vehicle_name",
              customName: "replacement_name",
              type: "string",
            },
            {
              name: "pickup_location",
              customName: "replacement_pickup",
              type: "string",
            },
          ],
        },
      }),

      // 6. Driver/manager notification (email)
      emailNode(
        "6",
        "Notify driver and manager of service",
        1240,
        "{driver_email}",
        "Vehicle Maintenance Scheduled - {vehicle_name} (Order: {service_order_id})",
        "Hi {driver_name},\\n\\nMaintenance has been scheduled for your vehicle.\\n\\nVehicle: {vehicle_name} (ID: {vehicle_id})\\nPriority: {maintenance_priority}\\nAlert Type: {alert_type}\\nCurrent Mileage: {current_mileage}\\nMiles Since Last Service: {miles_since_service}\\n\\nService Items: {maintenance_items}\\nService Date: {service_date}\\nService Shop: {service_shop}\\nEstimated Cost: ${estimated_cost}\\nService Order: {service_order_id}\\n\\nReplacement Vehicle: {replacement_name}\\nPickup Location: {replacement_pickup}\\n\\nPlease drop off your vehicle at the service shop by the scheduled date.",
      ),

      // 7. Parts pre-order
      n("7", "hitApi", "Pre-order maintenance parts (Shopify)", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "order",
              value:
                '{"line_items":[{"title":"{maintenance_items} - {vehicle_name}","quantity":1,"price":"{estimated_cost}"}],"note":"Fleet maintenance order for {vehicle_id} - Service Order {service_order_id}","tags":"fleet-maintenance"}',
            },
          ],
          variables: [
            { name: "order.id", customName: "parts_order_id", type: "string" },
            {
              name: "order.order_number",
              customName: "parts_order_number",
              type: "string",
            },
          ],
        },
      }),

      // 8. Maintenance completion logging (Sheets)
      sheetsNode(
        "8",
        "Log maintenance to fleet records (Sheets)",
        1720,
        "FleetMaintenance",
        '[["{today_date}","{vehicle_id}","{vehicle_name}","{driver_name}","{maintenance_priority}","{maintenance_items}","{current_mileage}","{service_order_id}","{service_date}","{estimated_cost}","{replacement_name}","{parts_order_number}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // immediate -> schedule ASAP
      e("3", "4", "output_2"), // not immediate -> still schedule
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 766. FLIGHT STATUS TRACKER BOT (8 nodes)
  // ===================================================================

  "Flight Status Tracker Bot": {
    summary:
      "Real-time flight status tracking and traveler assistance. A traveler inquiry trigger captures the request, FlightAware looks up flight status, a status classification condition routes delayed/cancelled flights, AI assesses delay impact, rebooking assistance is provided, push notifications keep travelers updated, airport amenity recommendations are shared, and a post-flight summary is sent.",
    nodes: [
      trigger("Webhook: Traveler flight status inquiry", "webhook_app", {
        source: "Travel App / Chat Widget / SMS",
        event: "flight.status.inquiry",
        howItStarts:
          "A traveler checks their flight status through the travel app, chat widget, or SMS command",
        setup:
          "Configure your travel app or chatbot to POST flight inquiries to the workflow URL. Include the traveler identity and flight number or booking reference.",
        inputFields: [],
      }),

      // 2. Extract + FlightAware status lookup
      aiNode(
        "2",
        "AI: Extract flight inquiry details",
        280,
        "You are a travel assistant. Parse the incoming flight inquiry and extract: traveler_name, traveler_email, traveler_phone, flight_number, departure_date, departure_city, arrival_city, booking_reference, and device_token (for push notifications). Respond JSON: {traveler_name: string, traveler_email: string, traveler_phone: string, flight_number: string, departure_date: string, departure_city: string, arrival_city: string, booking_reference: string, device_token: string}",
        "{webhook_payload}",
        [
          { field: "traveler_name", as: "traveler_name", type: "string" },
          { field: "traveler_email", as: "traveler_email", type: "string" },
          { field: "traveler_phone", as: "traveler_phone", type: "string" },
          { field: "flight_number", as: "flight_number", type: "string" },
          { field: "departure_date", as: "departure_date", type: "string" },
          { field: "departure_city", as: "departure_city", type: "string" },
          { field: "arrival_city", as: "arrival_city", type: "string" },
          {
            field: "booking_reference",
            as: "booking_reference",
            type: "string",
          },
          { field: "device_token", as: "device_token", type: "string" },
        ],
      ),

      // 3. FlightAware status lookup
      n("3", "hitApi", "Check flight status (FlightAware)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://aeroapi.flightaware.com/aeroapi/flights/{flight_number}?start={departure_date}",
          headers: [
            { key: "x-apikey", value: "{flightaware_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "flights[0].status",
              customName: "flight_status",
              type: "string",
            },
            {
              name: "flights[0].estimated_out",
              customName: "estimated_departure",
              type: "string",
            },
            {
              name: "flights[0].estimated_in",
              customName: "estimated_arrival",
              type: "string",
            },
            {
              name: "flights[0].gate_origin",
              customName: "departure_gate",
              type: "string",
            },
            {
              name: "flights[0].gate_destination",
              customName: "arrival_gate",
              type: "string",
            },
            {
              name: "flights[0].terminal_origin",
              customName: "departure_terminal",
              type: "string",
            },
            {
              name: "flights[0].filed_departuretime",
              customName: "scheduled_departure",
              type: "string",
            },
            {
              name: "flights[0].filed_arrivaltime",
              customName: "scheduled_arrival",
              type: "string",
            },
          ],
        },
      }),

      // 4. Status classification condition
      n("4", "condition", "Is flight delayed or cancelled?", 760, {
        conditionData: [
          {
            variable: "flight_status",
            value: "Scheduled",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI delay impact assessment
      aiNode(
        "5",
        "AI: Assess delay impact and provide guidance",
        1000,
        "You are a travel disruption specialist. Analyze the flight status and provide a comprehensive delay impact assessment. Include: delay duration estimate, connection risk analysis, rebooking options, passenger rights information, and practical advice. For cancellations, provide alternative flight suggestions. Respond JSON: {delay_minutes: number, impact_summary: string, rebooking_advice: string, passenger_rights: string, connection_risk: string, alternative_options: string, compensation_eligible: string}",
        "Flight: {flight_number}. Status: {flight_status}. Scheduled departure: {scheduled_departure}. Estimated departure: {estimated_departure}. From: {departure_city}. To: {arrival_city}. Gate: {departure_gate}. Terminal: {departure_terminal}.",
        [
          { field: "delay_minutes", as: "delay_minutes", type: "number" },
          { field: "impact_summary", as: "impact_summary", type: "string" },
          { field: "rebooking_advice", as: "rebooking_advice", type: "string" },
          { field: "passenger_rights", as: "passenger_rights", type: "string" },
          { field: "connection_risk", as: "connection_risk", type: "string" },
          {
            field: "alternative_options",
            as: "alternative_options",
            type: "string",
          },
          {
            field: "compensation_eligible",
            as: "compensation_eligible",
            type: "string",
          },
        ],
      ),

      // 6. Push notification (OneSignal)
      n(
        "6",
        "hitApi",
        "Send flight update push notification (OneSignal)",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://onesignal.com/api/v1/notifications",
            headers: [
              { key: "Authorization", value: "Basic {onesignal_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "app_id", value: "{onesignal_app_id}" },
              { key: "include_player_ids", value: '["{device_token}"]' },
              {
                key: "headings",
                value: '{"en":"Flight {flight_number} Update"}',
              },
              {
                key: "contents",
                value:
                  '{"en":"Status: {flight_status}. {impact_summary}. Gate: {departure_gate}, Terminal: {departure_terminal}."}',
              },
            ],
            variables: [
              { name: "id", customName: "notification_id", type: "string" },
              {
                name: "recipients",
                customName: "push_recipients",
                type: "number",
              },
            ],
          },
        },
      ),

      // 7. Airport amenity recommendations
      aiNode(
        "7",
        "AI: Recommend airport amenities",
        1480,
        "You are an airport concierge. Based on the delay duration and airport, recommend nearby amenities: lounges, restaurants, charging stations, rest areas, and shops. If the delay is long, suggest airport hotels. Include practical tips like Wi-Fi info and transit options. Respond JSON: {lounge_recommendation: string, dining_options: string, practical_tips: string, full_recommendation: string}",
        "Airport: {departure_city}. Terminal: {departure_terminal}. Gate: {departure_gate}. Delay: {delay_minutes} minutes. Flight: {flight_number} to {arrival_city}.",
        [
          {
            field: "lounge_recommendation",
            as: "lounge_recommendation",
            type: "string",
          },
          { field: "dining_options", as: "dining_options", type: "string" },
          { field: "practical_tips", as: "practical_tips", type: "string" },
          {
            field: "full_recommendation",
            as: "amenity_recommendations",
            type: "string",
          },
        ],
      ),

      // 8. Post-flight summary
      emailNode(
        "8",
        "Send post-flight summary email",
        1720,
        "{traveler_email}",
        "Flight {flight_number} Summary - {departure_city} to {arrival_city}",
        "Hi {traveler_name},\\n\\nHere is your flight summary for {flight_number}:\\n\\nRoute: {departure_city} -> {arrival_city}\\nStatus: {flight_status}\\nScheduled Departure: {scheduled_departure}\\nActual/Estimated: {estimated_departure}\\nGate: {departure_gate} | Terminal: {departure_terminal}\\n\\nDelay: {delay_minutes} minutes\\n{impact_summary}\\n\\nRebooking: {rebooking_advice}\\nPassenger Rights: {passenger_rights}\\nConnection Risk: {connection_risk}\\nCompensation: {compensation_eligible}\\n\\nAirport Recommendations:\\n{amenity_recommendations}\\n\\nSafe travels!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // delayed/cancelled -> impact assessment
      e("4", "6", "output_2"), // on time -> simple push notification
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 767. FREIGHT QUOTE REQUEST BOT (8 nodes)
  // ===================================================================

  "Freight Quote Request Bot": {
    summary:
      "Automated freight quote generation with multi-carrier rate shopping. A quote request trigger captures shipment details, runCode validates input and classifies NMFC codes, multi-carrier rate shopping queries carriers, AI compares rates and generates recommendations, a hazmat condition handles special requirements, quote acceptance and booking are processed, a delay manages quote expiration, and the CRM is updated with opportunity data.",
    nodes: [
      trigger("Webhook: Freight quote request", "webhook_app", {
        source: "Website / Customer Portal / Email",
        event: "freight.quote.requested",
        howItStarts:
          "A shipper requests a freight quote through the website form, customer portal, or forwarded email",
        setup:
          "Configure your freight quote form to POST shipment details to the workflow URL. Include origin, destination, commodity, weight, dimensions, and any special handling requirements.",
        inputFields: [],
      }),

      // 2. Input validation + NMFC classification (runCode)
      n("2", "runCode", "Validate input and classify NMFC", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse quote request and classify freight
const payload = JSON.parse(vars.webhook_payload || "{}");
const shipper = payload.shipper_name || payload.company || "";
const shipperEmail = payload.shipper_email || payload.email || "";
const shipperPhone = payload.shipper_phone || payload.phone || "";
const originZip = payload.origin_zip || payload.origin || "";
const destZip = payload.destination_zip || payload.destination || "";
const commodity = payload.commodity || payload.description || "";
const weight = parseFloat(payload.weight || 0);
const palletCount = parseInt(payload.pallets || payload.pallet_count || 1);
const lengthIn = parseFloat(payload.length || 48);
const widthIn = parseFloat(payload.width || 40);
const heightIn = parseFloat(payload.height || 48);
const isHazmat = (payload.hazmat || payload.is_hazmat || "false").toString().toLowerCase() === "true";
const hazmatClass = payload.hazmat_class || "";
const tempControl = (payload.temp_controlled || "false").toString().toLowerCase() === "true";
const liftgateRequired = (payload.liftgate || "false").toString().toLowerCase() === "true";

// Calculate density and estimate freight class
const cubicFeet = (lengthIn * widthIn * heightIn) / 1728;
const density = weight / cubicFeet;
let freightClass = "100";
if (density >= 35) freightClass = "60";
else if (density >= 22.5) freightClass = "65";
else if (density >= 15) freightClass = "70";
else if (density >= 12) freightClass = "77.5";
else if (density >= 10.5) freightClass = "85";
else if (density >= 8) freightClass = "92.5";
else if (density >= 6) freightClass = "100";
else if (density >= 4) freightClass = "125";
else if (density >= 2) freightClass = "175";
else freightClass = "200";

const accessorials = [];
if (isHazmat) accessorials.push("Hazmat");
if (tempControl) accessorials.push("Temperature controlled");
if (liftgateRequired) accessorials.push("Liftgate");

return {
  shipper_name: shipper,
  shipper_email: shipperEmail,
  shipper_phone: shipperPhone,
  origin_zip: originZip,
  destination_zip: destZip,
  commodity_description: commodity,
  total_weight: weight,
  pallet_count: palletCount,
  freight_class: freightClass,
  density: Math.round(density * 10) / 10,
  is_hazmat: isHazmat ? "yes" : "no",
  hazmat_class: hazmatClass,
  accessorials: accessorials.join(", ") || "None",
  quote_id: "FQ-" + Date.now().toString(36).toUpperCase(),
  is_valid: shipper && shipperEmail && originZip && destZip && weight > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "shipper_name", type: "string" },
            { name: "shipper_email", type: "string" },
            { name: "shipper_phone", type: "string" },
            { name: "origin_zip", type: "string" },
            { name: "destination_zip", type: "string" },
            { name: "commodity_description", type: "string" },
            { name: "total_weight", type: "number" },
            { name: "pallet_count", type: "number" },
            { name: "freight_class", type: "string" },
            { name: "density", type: "number" },
            { name: "is_hazmat", type: "string" },
            { name: "hazmat_class", type: "string" },
            { name: "accessorials", type: "string" },
            { name: "quote_id", type: "string" },
            { name: "is_valid", type: "string" },
          ],
        },
      }),

      // 3. Multi-carrier rate shopping
      n("3", "hitApi", "Get carrier rates (Freightview / ShipEngine)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.freightview.com/v1/rates",
          headers: [
            { key: "Authorization", value: "Bearer {freightview_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "origin_zip", value: "{origin_zip}" },
            { key: "destination_zip", value: "{destination_zip}" },
            {
              key: "items",
              value:
                '[{"weight":{total_weight},"freight_class":"{freight_class}","pallets":{pallet_count},"description":"{commodity_description}"}]',
            },
            { key: "accessorials", value: "{accessorials}" },
          ],
          variables: [
            { name: "rates", customName: "carrier_rates_json", type: "string" },
            { name: "rateCount", customName: "rate_count", type: "number" },
            {
              name: "cheapestRate",
              customName: "cheapest_rate",
              type: "number",
            },
            {
              name: "cheapestCarrier",
              customName: "cheapest_carrier",
              type: "string",
            },
            {
              name: "fastestTransitDays",
              customName: "fastest_transit",
              type: "number",
            },
            {
              name: "fastestCarrier",
              customName: "fastest_carrier",
              type: "string",
            },
          ],
        },
      }),

      // 4. Rate comparison + recommendation (AI)
      aiNode(
        "4",
        "AI: Compare rates and recommend",
        760,
        "You are a freight logistics advisor. Analyze the carrier rates and provide a recommendation. Consider cost, transit time, carrier reliability, and special requirements. Present top 3 options with pros/cons. If hazmat, ensure carrier is hazmat certified. Respond JSON: {recommended_carrier: string, recommended_rate: number, recommended_transit_days: number, recommendation_reason: string, option2_carrier: string, option2_rate: number, option3_carrier: string, option3_rate: number, rate_comparison_summary: string, savings_vs_average: number}",
        "Quote: {quote_id}. Origin: {origin_zip}, Dest: {destination_zip}. Weight: {total_weight} lbs, Class: {freight_class}. Hazmat: {is_hazmat} ({hazmat_class}). Accessorials: {accessorials}. Rates: {carrier_rates_json}. Cheapest: {cheapest_carrier} ${cheapest_rate}. Fastest: {fastest_carrier} ({fastest_transit} days).",
        [
          {
            field: "recommended_carrier",
            as: "recommended_carrier",
            type: "string",
          },
          { field: "recommended_rate", as: "recommended_rate", type: "number" },
          {
            field: "recommended_transit_days",
            as: "recommended_transit_days",
            type: "number",
          },
          {
            field: "recommendation_reason",
            as: "recommendation_reason",
            type: "string",
          },
          { field: "option2_carrier", as: "option2_carrier", type: "string" },
          { field: "option2_rate", as: "option2_rate", type: "number" },
          { field: "option3_carrier", as: "option3_carrier", type: "string" },
          { field: "option3_rate", as: "option3_rate", type: "number" },
          {
            field: "rate_comparison_summary",
            as: "rate_comparison",
            type: "string",
          },
          { field: "savings_vs_average", as: "savings_amount", type: "number" },
        ],
      ),

      // 5. Hazmat condition
      n("5", "condition", "Is this a hazmat shipment?", 1000, {
        conditionData: [
          {
            variable: "is_hazmat",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Quote delivery + booking (email)
      emailNode(
        "6",
        "Send freight quote to shipper",
        1240,
        "{shipper_email}",
        "Freight Quote {quote_id} - {origin_zip} to {destination_zip}",
        "Dear {shipper_name},\\n\\nHere is your freight quote:\\n\\nQuote ID: {quote_id}\\nOrigin: {origin_zip} -> Destination: {destination_zip}\\nCommodity: {commodity_description}\\nWeight: {total_weight} lbs | Class: {freight_class} | Pallets: {pallet_count}\\nDensity: {density} PCF\\nAccessorials: {accessorials}\\n\\nRECOMMENDED: {recommended_carrier}\\nRate: ${recommended_rate}\\nTransit: {recommended_transit_days} business days\\nWhy: {recommendation_reason}\\n\\nOther Options:\\n2. {option2_carrier}: ${option2_rate}\\n3. {option3_carrier}: ${option3_rate}\\n\\n{rate_comparison}\\n\\nSavings vs. average: ${savings_amount}\\n\\nTo book, reply with your preferred carrier or click:\\nhttps://book.example.com/quote/{quote_id}\\n\\nThis quote is valid for 48 hours.",
      ),

      // 7. Quote expiration management (delay)
      n("7", "delayAction", "Wait 48 hours for quote response", 1480, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 48,
          delayUnit: "hours",
        },
      }),

      // 8. CRM update (HubSpot)
      hubspotNode(
        "8",
        "Update CRM with quote data (HubSpot)",
        1720,
        "POST",
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Freight Quote {quote_id} - {shipper_name}","pipeline":"freight_quotes","dealstage":"quote_sent","amount":"{recommended_rate}","freight_origin":"{origin_zip}","freight_destination":"{destination_zip}","freight_class":"{freight_class}","freight_weight":"{total_weight}","carrier":"{recommended_carrier}","quote_id":"{quote_id}"}',
          },
        ],
        [
          { name: "id", customName: "deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // hazmat -> include hazmat details in quote
      e("5", "6", "output_2"), // standard -> send quote
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
